import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MessageService, MenuItem } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { TagModule } from 'primeng/tag';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { PaginatorModule } from 'primeng/paginator';
import { ToolbarModule } from 'primeng/toolbar';
import { DropdownModule } from 'primeng/dropdown';
import { MessagesModule } from 'primeng/messages';
import { DialogModule } from 'primeng/dialog';
import { CheckboxModule } from 'primeng/checkbox';
import { CalendarModule } from 'primeng/calendar';
import { BulkEmailValidationSyncItem, BulkResendDevisEmailResponse, BulkStatusProjectsResponse, PaginatedProjectResponse, ProjectHistoryExportOptions, ProjectHistoryFilters, ProjectHistoryItem, ProjectHistoryListItem, ProjectHistoryService, ProjectUserStatusCounts } from '../../services/project-history.service';
import { BANQUES_LIST } from '../../models/banque';
import { origines as origineOptions } from '../../models/enum';
import { SMS_RELANCE_SIMULATIONS_BODY } from '../../models/sms-templates';
import { AuthService } from 'src/app/shared/service/auth.service';
import { RingoverService } from '../../services/ringover.service';
import { TransactionService } from '../../services/transaction.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { RingoverClickToCallComponent } from '../shared/ringover-click-to-call/ringover-click-to-call.component';
import { User as UserProfile, UserService } from 'src/app/shared/service/user.service';

interface ProjectLoanLine {
  amount: number | null;
  duration: number | null;
  rate: number | null;
  label?: string;
}

@Component({
  selector: 'app-project-history',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    ToastModule,
    ConfirmDialogModule,
    TagModule,
    CardModule,
    InputTextModule,
    ProgressSpinnerModule,
    PaginatorModule,
    ToolbarModule,
    DropdownModule,
    MessagesModule,
    DialogModule,
    CheckboxModule,
    CalendarModule,
    MenuModule,
    RingoverClickToCallComponent
  ],
  templateUrl: './project-history.component.html',
  styleUrls: ['./project-history.component.css', './project-detail-dialog.css'],
  providers: [ConfirmationService]
})  
export class ProjectHistoryComponent implements OnInit {
  @ViewChild('csvImportInput') csvImportInput?: ElementRef<HTMLInputElement>;
  @ViewChild('mobileDeleteExcelInput') mobileDeleteExcelInput?: ElementRef<HTMLInputElement>;

  private readonly paginationStateStorageKey = 'projectHistoryPaginationState';
  private readonly filtersStateStorageKey = 'projectHistoryFiltersState';
  private currentProjectLoadKey = '';
  private lastProjectLoadKey = '';
  private readonly detailLabelOverrides: Record<string, string> = {
    id: 'ID interne',
    project_id: 'ID projet',
    user_id: 'Utilisateur',
    created_at: 'Date de création',
    tarif_mensuel_moins_cher: 'Cotisation mensuelle min',
    cotisation_totale_moins_chere: 'Cotisation totale min',
    assureur_moins_cher: 'Assureur (moins cher)',
    origine: 'Origine',
    projet_source_id: 'Projet source',
    type_souscripteur: 'Type de souscripteur',
    type_projet: 'Type de projet',
    banque: 'Banque',
    raison_sociale: 'Raison sociale',
    nom_projet: 'Projet',
    civilite: 'Civilité',
    nom: 'Nom',
    prenom: 'Prénom',
    adresse: 'Adresse',
    complement_adresse: "Complément d'adresse",
    code_postal: 'Code postal',
    ville: 'Ville',
    pays: 'Pays',
    telephone: 'Téléphone',
    email: 'Email',
    date_naissance: 'Date de naissance',
    date_effective: "Date d'effet",
    profession: 'Profession',
    professional_category: 'Catégorie professionnelle',
    fumeur: 'Fumeur',
    deplacement_professionnel: 'Déplacement professionnel',
    port_charge: 'Manutention',
    travaille_en_hauteur: 'Travail en hauteur',
    en_cours_total_assure: 'Encours assuré',
    est_co_emprunteur: 'Co-emprunteur',
    co_emprunteur_civilite: 'Co-emprunteur civilité',
    co_emprunteur_nom: 'Co-emprunteur nom',
    co_emprunteur_prenom: 'Co-emprunteur prénom',
    co_emprunteur_email: 'Co-emprunteur email',
    co_emprunteur_date_naissance: 'Co-emprunteur date de naissance',
    co_emprunteur_fumeur: 'Co-emprunteur fumeur',
    co_emprunteur_profession: 'Co-emprunteur profession',
    co_emprunteur_professional_category: 'Co-emprunteur catégorie professionnelle',
    co_emprunteur_deplacement_professionnel: 'Co-emprunteur déplacement professionnel',
    co_emprunteur_port_charge: 'Co-emprunteur manutention',
    co_emprunteur_travail_en_hauteur: 'Co-emprunteur travail en hauteur',
    co_emprunteur_en_cours_total_assure: 'Co-emprunteur encours assuré',
    type_pret: 'Type de prêt',
    montant: 'Montant',
    taux: 'Taux',
    duree: 'Durée',
    differe: 'Différé',
    type_differe: 'Type de différé',
    duree_palier: 'Durée palier',
    montant_echeance_palier: 'Montant échéance palier',
    paliers: 'Paliers',
    prets_additionnels: 'Prêts additionnels',
    quotite: 'Quotité',
    franchise_itt: 'Franchise ITT',
    frais_courtage: 'Frais de courtage',
    quotite_dc: 'Garantie DC',
    quotite_ptia: 'Garantie PTIA',
    quotite_itt: 'Garantie ITT',
    quotite_ipt: 'Garantie IPT',
    quotite_ipp: 'Garantie IPP',
    quotite_ippro: 'Garantie IPPRO',
    maux_dos: 'Maux de dos',
    psy: 'Maladies psychiques',
    co_emprunteur_quotite: 'Co-emprunteur quotité',
    co_emprunteur_franchise_itt: 'Co-emprunteur franchise ITT',
    co_emprunteur_frais_courtage: 'Co-emprunteur frais de courtage',
    co_emprunteur_quotite_dc: 'Co-emprunteur garantie DC',
    co_emprunteur_quotite_ptia: 'Co-emprunteur garantie PTIA',
    co_emprunteur_quotite_itt: 'Co-emprunteur garantie ITT',
    co_emprunteur_quotite_ipt: 'Co-emprunteur garantie IPT',
    co_emprunteur_quotite_ipp: 'Co-emprunteur garantie IPP',
    co_emprunteur_quotite_ippro: 'Co-emprunteur garantie IPPRO',
    co_emprunteur_maux_dos: 'Co-emprunteur maux de dos',
    co_emprunteur_psy: 'Co-emprunteur maladies psychiques',
    type_reprise: 'Type de reprise',
    reprise_avec_delegation: 'Reprise avec délégation',
    commentaire_emprunteur: 'Commentaire (Emprunteur)',
    commentaire_pret: 'Commentaire (Prêt)',
    commentaire_garanties: 'Commentaire (Garanties)'
  };

  private readonly detailFieldOrder: string[] = [
    'project_id',
    'created_at',
    'origine',
    'type_souscripteur',
    'type_projet',
    'banque',
    'nom_projet',
    'civilite',
    'prenom',
    'nom',
    'email',
    'telephone',
    'adresse',
    'complement_adresse',
    'code_postal',
    'ville',
    'pays',
    'date_naissance',
    'date_effective',
    'profession',
    'professional_category',
    'fumeur',
    'deplacement_professionnel',
    'port_charge',
    'travaille_en_hauteur',
    'en_cours_total_assure',
    'est_co_emprunteur',
    'co_emprunteur_civilite',
    'co_emprunteur_prenom',
    'co_emprunteur_nom',
    'co_emprunteur_email',
    'co_emprunteur_date_naissance',
    'co_emprunteur_profession',
    'co_emprunteur_professional_category',
    'co_emprunteur_fumeur',
    'co_emprunteur_deplacement_professionnel',
    'co_emprunteur_port_charge',
    'co_emprunteur_travail_en_hauteur',
    'co_emprunteur_en_cours_total_assure',
    'type_pret',
    'montant',
    'taux',
    'duree',
    'differe',
    'type_differe',
    'paliers',
    'prets_additionnels',
    'quotite',
    'franchise_itt',
    'quotite_dc',
    'quotite_ptia',
    'quotite_itt',
    'quotite_ipt',
    'quotite_ipp',
    'quotite_ippro',
    'maux_dos',
    'psy',
    'co_emprunteur_quotite',
    'co_emprunteur_franchise_itt',
    'co_emprunteur_quotite_dc',
    'co_emprunteur_quotite_ptia',
    'co_emprunteur_quotite_itt',
    'co_emprunteur_quotite_ipt',
    'co_emprunteur_quotite_ipp',
    'co_emprunteur_quotite_ippro',
    'co_emprunteur_maux_dos',
    'co_emprunteur_psy',
    'commentaire_emprunteur',
    'commentaire_pret',
    'commentaire_garanties',
    'projet_source_id',
    'id',
    'user_id'
  ];

  projects: ProjectHistoryListItem[] = [];
  filteredProjects: ProjectHistoryListItem[] = [];
  loading = false;
  searchTerm = '';
  isSearching = false;
  totalRecords = 0;
  totalATraiter = 0;
  totalTraite = 0;
  userStatusCounts: ProjectUserStatusCounts[] = [];
  rows = 10;
  first = 0;
  showProjectDetailDialog = false;
  selectedProjectDetails: ProjectHistoryItem | null = null;
  loadingDetails = false;
  importingCsv = false;
  exportingExcel = false;
  deletingSelection = false;
  deletingByMobileExcel = false;
  sendingBulkInfoEmail = false;
  sendingBulkSavingsEmail = false;
  sendingBulkSmsRappel = false;
  sendingBulkDevisResend = false;

  // Attribution de projets
  selectedProjects: ProjectHistoryListItem[] = [];
  allTableSelected = false;
  selectedBulkStatus: string | null = null;

  // Commentaire (source : Transaction.commentaire, identique agenda/transactions)
  commentDialogVisible = false;
  commentDialogTitle = '';
  commentDialogText = '';
  commentDialogProjectId: string | null = null;
  savingComment = false;

  // Menu d'actions unifié (Importer / Exporter / Attribuer / Changer statut / Supprimer...)
  selectedAction: string | null = null;
  showAssignmentDialog = false;
  activeUsers: any[] = [];
  selectedTargetUser: any = null;
  keepCopy = false;
  assignmentReason = '';
  assigningProjects = false;
  updatingStatus = false;

  // Options pour le filtre de type de projet
  typeProjetFilter = [
    { label: 'Résidence principale', value: 'residence_principale' },
    { label: 'Résidence secondaire', value: 'residence_secondaire' },
    { label: 'Investissement locatif', value: 'investissement_locatif' },
    { label: 'Terrain', value: 'TER' },
    { label: 'Construction', value: 'CONST' },
    { label: 'Prêt professionnel', value: 'pret_professionnel' },
    { label: 'Prêt consommation', value: 'pret_consommation' }
  ];

  selectedTypeProjet: string | null = null;

  // Options pour le filtre d'origine
  origineFilter = origineOptions;

  selectedOrigine: string | null = null;

  // Options pour le filtre de statut
  statusFilter = [
    { label: 'Nouveau', value: 'a_traiter' },
    { label: 'En cours', value: 'traite' },
    { label: 'Pas intéressé de suite', value: 'pas_interesse' },
    { label: 'À supprimer', value: 'a_supprimer' },
    { label: 'Trop cher', value: 'trop_cher' },
    { label: 'Médical', value: 'medical' },
    { label: 'Refus santé', value: 'refus_sante' }
  ];

  selectedStatus: string | null = null;

  // Options pour le filtre par utilisateur (attribution)
  usersFilter: any[] = [];
  selectedUserId: number | null = null;
  isAdmin = false;
  currentUserId: number | null = null;

  // Filtre par date de création
  dateRange: Date[] | null = null;
  selectedStartDate: Date | null = null;
  selectedEndDate: Date | null = null;

  // Filtre par validité de l'email
  emailValidityFilter = [
    { label: 'Email valide', value: 'valid' },
    { label: 'Email non valide', value: 'invalid' }
  ];
  selectedEmailValidity: 'valid' | 'invalid' | null = null;

  // Options pour le filtre par niveau de cotisation mensuelle
  cotisationLevelFilter = [
    { label: 'Cotisation < 20€', value: 'low' },
    { label: 'Cotisation 20€ - 60€', value: 'mid' },
    { label: 'Cotisation > 60€', value: 'high' }
  ];
  selectedCotisationLevel: 'low' | 'mid' | 'high' | null = null;

  private readonly emailValidationPattern = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
  private syncingEmailValidation = false;

  constructor(
    private projectHistoryService: ProjectHistoryService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private router: Router,
    private authService: AuthService,
    private ringoverService: RingoverService,
    private notificationService: NotificationService,
    private userService: UserService,
    private transactionService: TransactionService
  ) {
    // Commentaire modifié depuis une autre table (transactions, agenda).
    this.transactionService.commentaireChanged$.subscribe(({ project_id, commentaire }) => {
      this.projects
        .filter(p => p.project_id === project_id)
        .forEach(p => (p.commentaire = commentaire));
    });
  }

  /** Profil de l'agent connecté (pour la signature dynamique des SMS). */
  private agentProfile: UserProfile | null = null;

  /**
   * Construit les actions du menu déroulant de la barre d'outils
   * (Importer, Supprimer Excel, Exporter) — réservé aux administrateurs.
   */
  getToolbarMenuItems(): MenuItem[] {
    if (!this.isAdmin) {
      return [];
    }

    return [
      {
        label: 'Importer (CSV OGGO)',
        icon: 'pi pi-file-import',
        disabled: this.importingCsv,
        command: () => this.triggerCsvImport()
      },
      {
        label: 'Supprimer Excel',
        icon: 'pi pi-trash',
        disabled: this.deletingByMobileExcel,
        command: () => this.triggerMobileDeleteExcelImport()
      },
      {
        label: 'Exporter Excel',
        icon: 'pi pi-file-excel',
        disabled: this.exportingExcel,
        command: () => this.exportHistoryToExcel()
      }
    ];
  }

  /**
   * Indique si une action de la barre d'outils est en cours (libellé/loader du bouton).
   */
  isToolbarActionBusy(): boolean {
    return this.importingCsv || this.deletingByMobileExcel || this.exportingExcel;
  }

  /**
   * Envoie l'email de demande d'information aux projets sélectionnés en une seule action.
   */
  sendBulkInformationRequest() {
    if (this.allTableSelected) {
      this.messageService.add({
        severity: 'info',
        summary: 'Sélection complète',
        detail: 'La demande d\'information groupée est disponible pour une sélection explicite de projets.'
      });
      return;
    }

    if (this.sendingBulkInfoEmail) {
      return;
    }

    if (!this.selectedProjects.every(project => this.canSelectProject(project))) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Accès refusé',
        detail: 'Vous pouvez envoyer une demande d\'information uniquement pour vos projets.'
      });
      return;
    }

    const projectsWithEmail = this.selectedProjects.filter(project => (project.email || '').trim().length > 0);
    const projectsWithoutEmail = this.selectedProjects.length - projectsWithEmail.length;

    if (projectsWithEmail.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Aucun destinataire',
        detail: 'Aucun projet sélectionné ne possède d\'adresse email.'
      });
      return;
    }

    const projectIds = projectsWithEmail.map(project => project.project_id);
    const recipientLabel = projectIds.length === 1
      ? `le projet "${this.getProjectDisplayName(projectsWithEmail[0])}"`
      : `${projectIds.length} projet(s)`;
    const withoutEmailNote = projectsWithoutEmail > 0
      ? ` (${projectsWithoutEmail} projet(s) sans email seront ignorés)`
      : '';

    this.confirmationService.confirm({
      message: `Envoyer l'email de demande d'information à ${recipientLabel} ?${withoutEmailNote}`,
      header: 'Confirmer l\'envoi groupé',
      icon: 'pi pi-envelope',
      acceptLabel: 'Envoyer',
      rejectLabel: 'Annuler',
      accept: () => {
        this.sendingBulkInfoEmail = true;

        this.projectHistoryService.sendBulkStepOneEmail(projectIds).subscribe({
          next: (response) => {
            const hasIssues = response.failed_count > 0 || response.skipped_no_email_count > 0;
            const issueParts: string[] = [];
            if (response.failed_count > 0) {
              issueParts.push(`${response.failed_count} échec(s)`);
            }
            if (response.skipped_no_email_count > 0) {
              issueParts.push(`${response.skipped_no_email_count} sans email`);
            }
            if (response.not_found_count > 0) {
              issueParts.push(`${response.not_found_count} introuvable(s)`);
            }
            const issuesSuffix = issueParts.length ? ` (${issueParts.join(', ')})` : '';

            this.messageService.add({
              severity: hasIssues ? 'warn' : 'success',
              summary: hasIssues ? 'Envoi partiel' : 'Demandes d\'information envoyées',
              detail: `${response.sent_count} email(s) envoyé(s)${issuesSuffix}.`,
              life: 10000
            });

            this.sendingBulkInfoEmail = false;
          },
          error: (error) => {
            console.error('Erreur lors de l\'envoi groupé de demande d\'information:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Erreur',
              detail: error?.error?.detail || 'Impossible d\'envoyer les demandes d\'information.'
            });
            this.sendingBulkInfoEmail = false;
          }
        });
      }
    });
  }

  /**
   * Envoie l'email d'économies aux projets sélectionnés en une seule action.
   */
  sendBulkSavingsEmailRequest() {
    if (this.allTableSelected) {
      this.messageService.add({
        severity: 'info',
        summary: 'Sélection complète',
        detail: 'L\'envoi groupé de l\'email d\'économies est disponible pour une sélection explicite de projets.'
      });
      return;
    }

    if (this.sendingBulkSavingsEmail) {
      return;
    }

    if (!this.selectedProjects.every(project => this.canSelectProject(project))) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Accès refusé',
        detail: 'Vous pouvez envoyer un email d\'économies uniquement pour vos projets.'
      });
      return;
    }

    const projectsWithEmail = this.selectedProjects.filter(project => (project.email || '').trim().length > 0);
    const projectsWithoutEmail = this.selectedProjects.length - projectsWithEmail.length;

    if (projectsWithEmail.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Aucun destinataire',
        detail: 'Aucun projet sélectionné ne possède d\'adresse email.'
      });
      return;
    }

    const projectIds = projectsWithEmail.map(project => project.project_id);
    const recipientLabel = projectIds.length === 1
      ? `le projet "${this.getProjectDisplayName(projectsWithEmail[0])}"`
      : `${projectIds.length} projet(s)`;
    const withoutEmailNote = projectsWithoutEmail > 0
      ? ` (${projectsWithoutEmail} projet(s) sans email seront ignorés)`
      : '';

    this.confirmationService.confirm({
      message: `Envoyer l'email d'économies à ${recipientLabel} ?${withoutEmailNote}`,
      header: 'Confirmer l\'envoi groupé',
      icon: 'pi pi-percentage',
      acceptLabel: 'Envoyer',
      rejectLabel: 'Annuler',
      accept: () => {
        this.sendingBulkSavingsEmail = true;

        this.projectHistoryService.sendBulkStepOneSavingsEmail(projectIds).subscribe({
          next: (response) => {
            const hasIssues = response.failed_count > 0 || response.skipped_no_email_count > 0;
            const issueParts: string[] = [];
            if (response.failed_count > 0) {
              issueParts.push(`${response.failed_count} échec(s)`);
            }
            if (response.skipped_no_email_count > 0) {
              issueParts.push(`${response.skipped_no_email_count} sans email`);
            }
            if (response.not_found_count > 0) {
              issueParts.push(`${response.not_found_count} introuvable(s)`);
            }
            const issuesSuffix = issueParts.length ? ` (${issueParts.join(', ')})` : '';

            this.messageService.add({
              severity: hasIssues ? 'warn' : 'success',
              summary: hasIssues ? 'Envoi partiel' : 'Emails d\'économies envoyés',
              detail: `${response.sent_count} email(s) envoyé(s)${issuesSuffix}.`,
              life: 10000
            });

            this.sendingBulkSavingsEmail = false;
          },
          error: (error) => {
            console.error('Erreur lors de l\'envoi groupé de l\'email d\'économies:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Erreur',
              detail: error?.error?.detail || 'Impossible d\'envoyer les emails d\'économies.'
            });
            this.sendingBulkSavingsEmail = false;
          }
        });
      }
    });
  }

  /**
   * Action unifiée "Renvoyer SMS + Devis" sur la sélection :
   * - renvoie le mail devis déjà envoyé (Gmail) aux projets traités avec offre + email ;
   * - envoie le SMS "Rappel Devis" aux projets disposant d'un téléphone.
   * Une seule confirmation, un récapitulatif combiné.
   */
  sendBulkSmsAndDevisRequest(): void {
    if (this.allTableSelected) {
      this.messageService.add({
        severity: 'info',
        summary: 'Sélection complète',
        detail: 'L\'envoi groupé SMS + Devis est disponible pour une sélection explicite de projets.'
      });
      return;
    }

    if (this.sendingBulkDevisResend || this.sendingBulkSmsRappel) {
      return;
    }

    if (!this.selectedProjects.every(project => this.canSelectProject(project))) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Accès refusé',
        detail: 'Vous pouvez agir uniquement sur vos projets.'
      });
      return;
    }

    const devisProjects = this.selectedProjects.filter(project =>
      this.normalizeStatusValue(project.status) === 'traite'
      && (project.email || '').trim().length > 0
    );

    // Dédoublonnage des SMS par numéro : une même personne (ex. plusieurs projets
    // avec le même téléphone) ne doit recevoir qu'un seul SMS.
    const phoneProjects: ProjectHistoryListItem[] = [];
    const seenPhones = new Set<string>();
    for (const project of this.selectedProjects) {
      const phone = this.projectPhoneE164(project);
      if (!phone || seenPhones.has(phone)) {
        continue;
      }
      seenPhones.add(phone);
      phoneProjects.push(project);
    }

    if (devisProjects.length === 0 && phoneProjects.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Aucune action possible',
        detail: 'Aucun projet sélectionné n\'a de téléphone ni de devis renvoyable (traité + offre + email).'
      });
      return;
    }

    const devisIds = devisProjects.map(project => project.project_id);
    const actionParts: string[] = [];
    if (phoneProjects.length > 0) {
      actionParts.push(`un SMS "Rappel Devis" à ${phoneProjects.length} contact(s)`);
    }
    if (devisIds.length > 0) {
      actionParts.push(`renvoyer le devis à ${devisIds.length} projet(s)`);
    }

    this.confirmationService.confirm({
      message: `Envoyer ${actionParts.join(' et ')} ?`,
      header: 'Confirmer l\'envoi SMS + Devis',
      icon: 'pi pi-send',
      acceptLabel: 'Envoyer',
      rejectLabel: 'Annuler',
      accept: () => {
        const state = {
          smsPending: phoneProjects.length > 0,
          devisPending: devisIds.length > 0,
          smsSent: 0,
          smsFailed: 0,
          devisResponse: null as BulkResendDevisEmailResponse | null,
          devisError: null as any,
        };

        this.sendingBulkSmsRappel = state.smsPending;
        this.sendingBulkDevisResend = state.devisPending;

        const finalize = () => {
          if (state.smsPending || state.devisPending) {
            return;
          }
          this.sendingBulkSmsRappel = false;
          this.sendingBulkDevisResend = false;

          const lines: string[] = [];
          let hasIssues = false;

          if (phoneProjects.length > 0) {
            lines.push(`SMS : ${state.smsSent} envoyé(s)${state.smsFailed > 0 ? ` (${state.smsFailed} échec(s))` : ''}`);
            hasIssues = hasIssues || state.smsFailed > 0;
          }

          if (devisIds.length > 0) {
            if (state.devisError) {
              const detail = state.devisError?.error?.detail;
              const message = typeof detail === 'object' && detail?.message
                ? detail.message
                : (typeof detail === 'string' ? detail : 'erreur');
              lines.push(`Devis : échec (${message})`);
              hasIssues = true;
            } else if (state.devisResponse) {
              const response = state.devisResponse;
              const issueParts: string[] = [];
              if (response.failed_count > 0) {
                issueParts.push(`${response.failed_count} échec(s)`);
              }
              if (response.not_found_in_gmail_count > 0) {
                issueParts.push(`${response.not_found_in_gmail_count} introuvable(s) Gmail`);
              }
              if (response.duplicate_count > 0) {
                issueParts.push(`${response.duplicate_count} doublon(s) évité(s)`);
              }
              if (response.skipped_no_email_count > 0) {
                issueParts.push(`${response.skipped_no_email_count} sans email`);
              }
              if (response.not_eligible_count > 0) {
                issueParts.push(`${response.not_eligible_count} non éligible(s)`);
              }
              lines.push(`Devis : ${response.sent_count} renvoyé(s)${issueParts.length ? ` (${issueParts.join(', ')})` : ''}`);
              hasIssues = hasIssues || issueParts.length > 0;
            }
          }

          this.messageService.add({
            severity: hasIssues ? 'warn' : 'success',
            summary: hasIssues ? 'Envoi partiel' : 'SMS + Devis envoyés',
            detail: lines.join(' — '),
            life: 12000
          });
        };

        if (state.devisPending) {
          this.runBulkDevisResend(devisIds, (response, error) => {
            state.devisResponse = response;
            state.devisError = error;
            state.devisPending = false;
            finalize();
          });
        }

        if (state.smsPending) {
          this.runBulkSmsRappel(phoneProjects, (sent, failed) => {
            state.smsSent = sent;
            state.smsFailed = failed;
            state.smsPending = false;
            finalize();
          });
        }
      }
    });
  }

  /** Renvoi groupé des devis (Gmail) — exécution sans confirmation. */
  private runBulkDevisResend(
    projectIds: string[],
    done: (response: BulkResendDevisEmailResponse | null, error: any) => void
  ): void {
    this.projectHistoryService.bulkResendDevisEmail(projectIds).subscribe({
      next: (response: BulkResendDevisEmailResponse) => done(response, null),
      error: (error) => {
        console.error('Erreur lors du renvoi groupé des devis:', error);
        done(null, error);
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Ringover — helpers + actions SMS
  // ---------------------------------------------------------------------------

  /** Normalise un numéro en E.164 avec '+'. Accepte 0612…, +33612…, 33612…, 0033612… */
  projectPhoneE164(project: ProjectHistoryListItem): string | null {
    const raw = (project.telephone || '').trim();
    if (!raw) return null;
    const digits = raw.replace(/\D/g, '');
    if (!digits || digits.length < 8) return null;
    if (digits.startsWith('00')) return `+${digits.slice(2)}`;
    if (digits.startsWith('33')) return `+${digits}`;
    if (digits.startsWith('0')) return `+33${digits.slice(1)}`;
    return `+${digits}`;
  }

  projectDisplayName(project: ProjectHistoryListItem): string {
    return [project.prenom, project.nom].filter(Boolean).join(' ') || project.raison_sociale || '';
  }

  /** Ouvre le commentaire de la transaction liée au projet. */
  openComment(project: ProjectHistoryListItem): void {
    this.commentDialogTitle = [this.projectDisplayName(project), project.project_id]
      .filter(Boolean).join(' · ');
    this.commentDialogText = (project.commentaire || '').trim();
    this.commentDialogProjectId = project.project_id || null;
    this.commentDialogVisible = true;
  }

  /** Enregistre le commentaire : la table transactions et l'agenda suivent. */
  saveComment(): void {
    const projectId = this.commentDialogProjectId;
    if (!projectId || this.savingComment) return;
    const value = this.commentDialogText.trim();
    this.savingComment = true;
    this.transactionService.updateCommentaireByProject(projectId, value).subscribe({
      next: () => {
        this.savingComment = false;
        this.commentDialogVisible = false;
        this.messageService.add({ severity: 'success', summary: 'Commentaire enregistré', life: 2000 });
      },
      error: (error) => {
        this.savingComment = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Enregistrement échoué',
          detail: error?.error?.detail || 'Impossible de modifier le commentaire.'
        });
      }
    });
  }

  /** Renvoie "Cher" ou "Chère" selon la civilité. */
  private buildCiviliteGreeting(civilite: string | undefined): string {
    const civ = (civilite || '').trim().toLowerCase().replace(/\./g, '');
    return ['mme', 'madame', 'mlle', 'mademoiselle'].includes(civ) ? 'Chère' : 'Cher';
  }

  private formatCiviliteForSms(civilite: string | undefined): string {
    const raw = (civilite || '').trim();
    const normalized = raw.toLowerCase().replace(/\./g, '');
    if (['mme', 'madame', 'mlle', 'mademoiselle'].includes(normalized)) return 'Mme';
    if (['m', 'mr', 'monsieur'].includes(normalized)) return 'M.';
    return raw;
  }

  /** Signature dynamique de l'agent connecté. */
  private buildAgentSignature(agent: UserProfile): string {
    const lines: string[] = [];
    const name = (agent.username || '').trim();
    if (name) lines.push(name);
    if (agent.telephone_fixe) lines.push(agent.telephone_fixe.trim());
    if (agent.telephone) lines.push(agent.telephone.trim());
    if (agent.email) lines.push(agent.email.trim());
    lines.push('Cabinet BROKINS');
    lines.push('www.brokins.fr');
    return lines.join('\n');
  }

  /** Construit le contenu SMS "Rappel Devis" dynamique pour un projet donné. */
  private buildSmsRappelContent(project: ProjectHistoryListItem, signature: string): string {
    const civilite = (project.civilite || '').trim();
    const greeting = this.buildCiviliteGreeting(civilite);
    const smsCivilite = this.formatCiviliteForSms(civilite);
    const nom = (project.nom || '').trim();
    const salutation = `${greeting}${smsCivilite ? ' ' + smsCivilite : ''}${nom ? ' ' + nom : ''}`;
    return `${salutation},\n\n${SMS_RELANCE_SIMULATIONS_BODY}\n\nBien à vous\n\n${signature}`;
  }

  /**
   * Envoi groupé des SMS "Rappel Devis" — exécution sans confirmation.
   * Récupère la signature de l'agent une seule fois puis envoie un SMS par projet.
   */
  private runBulkSmsRappel(
    projectsWithPhone: ProjectHistoryListItem[],
    done: (sent: number, failed: number) => void
  ): void {
    if (projectsWithPhone.length === 0) {
      done(0, 0);
      return;
    }

    const launch = (agent: UserProfile) => {
      const signature = this.buildAgentSignature(agent);
      let sent = 0; let failed = 0;
      const total = projectsWithPhone.length;

      projectsWithPhone.forEach(project => {
        const phone = this.projectPhoneE164(project)!;
        this.ringoverService.sendSms({
          to_number: phone,
          content: this.buildSmsRappelContent(project, signature)
        }).subscribe({
          next: () => {
            sent++;
            if (sent + failed === total) done(sent, failed);
          },
          error: () => {
            failed++;
            if (sent + failed === total) done(sent, failed);
          }
        });
      });
    };

    if (this.agentProfile) {
      launch(this.agentProfile);
    } else {
      this.userService.getMyProfile().subscribe({
        next: (p) => { this.agentProfile = p; launch(p); },
        error: () => launch({ id: 0, username: '', email: '', is_active: true, role: 'USER', createdAt: null })
      });
    }
  }

  /** SMS Devis Envoyé — bouton individuel dans la cellule cotisation. */
  sendSmsDevisEnvoye(project: ProjectHistoryListItem): void {
    const phone = this.projectPhoneE164(project);
    if (!phone) return;
    const name = project.nom || project.raison_sociale || '';
    this.confirmationService.confirm({
      message: `Envoyer un SMS "Devis Envoyé" à ${name || phone} (${phone}) ?`,
      header: 'Confirmer l\'envoi SMS',
      icon: 'pi pi-comment',
      acceptLabel: 'Envoyer',
      rejectLabel: 'Annuler',
      accept: () => {
        this.ringoverService.sendSms({
          to_number: phone,
          content: `Bonjour${name ? ' ' + name + ',' : ','} votre devis assurance emprunteur vient de vous être envoyé par email. N'hésitez pas à nous contacter.`
        }).subscribe({
          next: () => this.notificationService.addNotification('success', 'SMS envoyé', `SMS "Devis Envoyé" transmis à ${name || phone}.`),
          error: (err) => this.notificationService.addNotification('error', 'Échec SMS', err?.error?.detail || 'Erreur lors de l\'envoi du SMS.')
        });
      }
    });
  }

  triggerCsvImport() {
    if (!this.isAdmin || this.importingCsv) {
      return;
    }
    this.csvImportInput?.nativeElement.click();
  }

  onCsvFileSelected(event: Event) {
    if (!this.isAdmin) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Accès refusé',
        detail: 'Seuls les administrateurs peuvent importer des projets.'
      });
      return;
    }

    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];

    if (!file) {
      return;
    }

    const lower = file.name.toLowerCase();
    if (!lower.endsWith('.csv') && !lower.endsWith('.txt')) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Format invalide',
        detail: 'Veuillez sélectionner un fichier CSV.'
      });
      input.value = '';
      return;
    }

    this.importingCsv = true;
    this.projectHistoryService.importProjectsCsv(file).subscribe({
      next: (result) => {
        const errorPreview = result.errors?.length
          ? ` ${Math.min(result.errors.length, 3)} erreur(s) exemple: ${result.errors.slice(0, 3).join(' | ')}`
          : '';
        const doublonInfo = result.doublons > 0 ? `, ${result.doublons} doublon(s)` : '';

        this.messageService.add({
          severity: result.skipped > 0 ? 'warn' : 'success',
          summary: 'Import terminé',
          detail: `${result.imported} projet(s) importé(s), ${result.skipped} ignoré(s)${doublonInfo}.${errorPreview}`,
          life: 12000
        });

        this.refresh();
        this.importingCsv = false;
        input.value = '';
      },
      error: (error) => {
        console.error('Erreur import CSV:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Import échoué',
          detail: error?.error?.detail || 'Impossible d\'importer le fichier CSV.'
        });
        this.importingCsv = false;
        input.value = '';
      }
    });
  }

  triggerMobileDeleteExcelImport() {
    if (!this.isAdmin || this.deletingByMobileExcel) {
      return;
    }
    this.mobileDeleteExcelInput?.nativeElement.click();
  }

  onMobileDeleteExcelSelected(event: Event) {
    if (!this.isAdmin) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Acces refuse',
        detail: 'Seuls les administrateurs peuvent supprimer par fichier Excel.'
      });
      return;
    }

    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];

    if (!file) {
      return;
    }

    const lower = file.name.toLowerCase();
    if (!lower.endsWith('.xlsx') && !lower.endsWith('.xlsm')) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Format invalide',
        detail: 'Veuillez selectionner un fichier Excel .xlsx ou .xlsm avec les colonnes de suppression.'
      });
      input.value = '';
      return;
    }

    this.confirmationService.confirm({
      message: `Importer "${file.name}" ? Les numeros de la colonne "Projets à Supprimer" supprimeront les projets. Les numeros de la colonne "Numéro a effacer dans les projets" videront seulement le telephone des projets correspondants.`,
      header: 'Confirmer la suppression par Excel',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Executer',
      rejectLabel: 'Annuler',
      accept: () => {
        this.deletingByMobileExcel = true;

        this.projectHistoryService.deleteProjectsByMobileExcel(file).subscribe({
          next: (response) => {
            this.removeProjectsFromState(response.deleted_project_ids);

            const notFoundDeleteCount = response.not_found_delete_phones?.length ?? response.not_found_mobiles.length;
            const notFoundClearCount = response.not_found_clear_phones?.length ?? 0;
            const phoneClearedCount = response.phone_cleared_count ?? 0;
            const uniqueDeleteCount = response.unique_delete_phone_count ?? response.unique_mobile_count;
            const uniqueClearCount = response.unique_clear_phone_count ?? 0;
            const notFoundInfo = notFoundDeleteCount + notFoundClearCount > 0
              ? ` ${notFoundDeleteCount} numero(s) a supprimer introuvable(s), ${notFoundClearCount} numero(s) a effacer introuvable(s).`
              : '';

            this.messageService.add({
              severity: notFoundDeleteCount + notFoundClearCount > 0 ? 'warn' : 'success',
              summary: notFoundDeleteCount + notFoundClearCount > 0 ? 'Traitement partiel' : 'Traitement termine',
              detail: `${response.deleted_count} projet(s) supprime(s) depuis ${uniqueDeleteCount} numero(s) unique(s). ${phoneClearedCount} telephone(s) efface(s) depuis ${uniqueClearCount} numero(s) unique(s).${notFoundInfo}`,
              life: 12000
            });

            this.refresh();
            this.deletingByMobileExcel = false;
            input.value = '';
          },
          error: (error) => {
            console.error('Erreur traitement par Excel:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Traitement echoue',
              detail: error?.error?.detail || 'Impossible de traiter le fichier Excel.'
            });
            this.deletingByMobileExcel = false;
            input.value = '';
          }
        });
      },
      reject: () => {
        input.value = '';
      }
    });
  }

  exportHistoryToExcel() {
    if (!this.isAdmin) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Accès refusé',
        detail: 'Seuls les administrateurs peuvent exporter l\'historique.'
      });
      return;
    }

    if (this.exportingExcel) {
      return;
    }

    const selectedProjectIds = this.selectedProjects.map(project => project.project_id);
    const useSelection = selectedProjectIds.length > 0 && !this.allTableSelected;
    const exportProjectIds = useSelection ? selectedProjectIds : [];
    const exportOptions: ProjectHistoryExportOptions = useSelection
      ? {}
      : {
        ...this.buildServerFilters(),
        export_all_matching: true
      };

    if (useSelection && exportProjectIds.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Export',
        detail: 'Aucun projet à exporter.'
      });
      return;
    }

    this.exportingExcel = true;

    this.projectHistoryService.exportProjectHistoryExcel(exportProjectIds, exportOptions).subscribe({
      next: (response) => {
        const blob = response.body;
        if (!blob) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Export',
            detail: 'Aucun fichier à télécharger.'
          });
          this.exportingExcel = false;
          return;
        }

        const contentDisposition = response.headers.get('content-disposition');
        const filename = this.extractFilenameFromContentDisposition(contentDisposition)
          || `historique_projets_${new Date().toISOString().slice(0, 10)}.xlsx`;

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.messageService.add({
          severity: 'success',
          summary: 'Export terminé',
          detail: !useSelection
            ? `${this.totalRecords} projet(s) du tableau exporté(s).`
            : `${selectedProjectIds.length} projet(s) sélectionné(s) exporté(s).`
        });

        this.exportingExcel = false;
      },
      error: (error) => {
        console.error('Erreur lors de l\'export Excel:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Impossible d\'exporter l\'historique en Excel'
        });
        this.exportingExcel = false;
      }
    });
  }

  private extractFilenameFromContentDisposition(contentDisposition: string | null): string | null {
    if (!contentDisposition) {
      return null;
    }

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match && utf8Match[1]) {
      return decodeURIComponent(utf8Match[1].replace(/['"]/g, '').trim());
    }

    const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    if (filenameMatch && filenameMatch[1]) {
      return filenameMatch[1].trim();
    }

    return null;
  }

  ngOnInit() {
    console.log('🚀 Initialisation du composant Project History');
    console.log(`� Configuration pagination: ${this.rows} projets par page`);

    this.restorePaginationState();
    this.restoreFiltersState();
    this.currentUserId = this.getCurrentUserIdFromToken();

    // Vérifier si l'utilisateur est admin
    this.isAdmin = this.checkAdminStatus();

    // D'abord, essayer de récupérer le nombre total
    this.loadTotalCount();

    // Ensuite charger la première page
    this.loadProjects(0, this.rows);
  }

  private restorePaginationState(): void {
    try {
      const rawState = sessionStorage.getItem(this.paginationStateStorageKey);
      if (!rawState) {
        return;
      }

      const parsed = JSON.parse(rawState) as { first?: number; rows?: number };
      const restoredFirst = Number(parsed?.first);
      const restoredRows = Number(parsed?.rows);

      if (Number.isFinite(restoredFirst) && restoredFirst >= 0) {
        this.first = restoredFirst;
      }

      if (Number.isFinite(restoredRows) && restoredRows > 0) {
        this.rows = restoredRows;
      }
    } catch (error) {
      console.warn('Impossible de restaurer la pagination Project History:', error);
      sessionStorage.removeItem(this.paginationStateStorageKey);
    }
  }

  private persistPaginationState(): void {
    const state = {
      first: this.first,
      rows: this.rows,
    };

    sessionStorage.setItem(this.paginationStateStorageKey, JSON.stringify(state));
  }

  private restoreFiltersState(): void {
    try {
      const rawState = sessionStorage.getItem(this.filtersStateStorageKey);
      if (!rawState) {
        return;
      }

      const parsed = JSON.parse(rawState) as {
        searchTerm?: string;
        selectedTypeProjet?: string | null;
        selectedOrigine?: string | null;
        selectedStatus?: string | null;
        selectedEmailValidity?: 'valid' | 'invalid' | null;
        selectedCotisationLevel?: 'low' | 'mid' | 'high' | null;
        selectedUserId?: number | null;
        dateRange?: string[] | null;
      };

      this.searchTerm = typeof parsed?.searchTerm === 'string' ? parsed.searchTerm : '';
      this.selectedTypeProjet = parsed?.selectedTypeProjet ?? null;
      this.selectedOrigine = parsed?.selectedOrigine ?? null;
      this.selectedStatus = parsed?.selectedStatus ?? null;
      this.selectedEmailValidity = parsed?.selectedEmailValidity === 'valid' || parsed?.selectedEmailValidity === 'invalid'
        ? parsed.selectedEmailValidity
        : null;
      this.selectedCotisationLevel = parsed?.selectedCotisationLevel === 'low'
        || parsed?.selectedCotisationLevel === 'mid'
        || parsed?.selectedCotisationLevel === 'high'
        ? parsed.selectedCotisationLevel
        : null;

      const restoredUserId = Number(parsed?.selectedUserId);
      this.selectedUserId = Number.isFinite(restoredUserId) && restoredUserId > 0
        ? restoredUserId
        : null;

      if (Array.isArray(parsed?.dateRange) && parsed.dateRange.length === 2) {
        const [startRaw, endRaw] = parsed.dateRange;
        const startDate = startRaw ? new Date(startRaw) : null;
        const endDate = endRaw ? new Date(endRaw) : null;

        if (startDate && endDate && !isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
          this.dateRange = [startDate, endDate];
          this.selectedStartDate = startDate;
          this.selectedEndDate = endDate;
        } else {
          this.dateRange = null;
          this.selectedStartDate = null;
          this.selectedEndDate = null;
        }
      }
    } catch (error) {
      console.warn('Impossible de restaurer les filtres Project History:', error);
      sessionStorage.removeItem(this.filtersStateStorageKey);
    }
  }

  private persistFiltersState(): void {
    const normalizedDateRange =
      this.dateRange && this.dateRange.length === 2 && this.dateRange[0] && this.dateRange[1]
        ? [this.dateRange[0].toISOString(), this.dateRange[1].toISOString()]
        : null;

    const state = {
      searchTerm: this.searchTerm,
      selectedTypeProjet: this.selectedTypeProjet,
      selectedOrigine: this.selectedOrigine,
      selectedStatus: this.selectedStatus,
      selectedEmailValidity: this.selectedEmailValidity,
      selectedCotisationLevel: this.selectedCotisationLevel,
      selectedUserId: this.selectedUserId,
      dateRange: normalizedDateRange,
    };

    sessionStorage.setItem(this.filtersStateStorageKey, JSON.stringify(state));
  }

  /**
   * Vérifie si l'utilisateur actuel est administrateur
   */
  checkAdminStatus(): boolean {
    const role = localStorage.getItem('role');
    return this.authService.isAdmin() || role === 'ADMIN' || role === 'admin';
  }

  private getCurrentUserIdFromToken(): number | null {
    const token = localStorage.getItem('access_token');
    if (!token) {
      return null;
    }

    try {
      const payload = token.split('.')[1];
      if (!payload) {
        return null;
      }

      const decoded = JSON.parse(atob(payload));
      const userId = Number(decoded?.user_id);
      return Number.isFinite(userId) && userId > 0 ? userId : null;
    } catch (error) {
      console.warn('Impossible de décoder user_id depuis le token JWT:', error);
      return null;
    }
  }

  private getCurrentAdminAssignee(): { id: number; full_name: string; email: string; project_count: number } | null {
    if (!this.isAdmin || this.currentUserId === null) {
      return null;
    }

    const token = localStorage.getItem('access_token');
    const fallback = {
      id: this.currentUserId,
      full_name: 'Moi-meme (admin)',
      email: '',
      project_count: 0,
    };

    if (!token) {
      return fallback;
    }

    try {
      const payload = token.split('.')[1];
      if (!payload) {
        return fallback;
      }

      const decoded = JSON.parse(atob(payload));
      const fullName = String(decoded?.full_name || decoded?.name || '').trim();
      const email = String(decoded?.email || '').trim();

      return {
        ...fallback,
        full_name: fullName ? `${fullName} (moi)` : fallback.full_name,
        email,
      };
    } catch {
      return fallback;
    }
  }

  /**
   * Charge la liste des utilisateurs pour le filtre
   */
  loadUsersForFilter() {
    if (this.userStatusCounts.length > 0) {
      this.usersFilter = this.userStatusCounts
        .filter(counts => Number(counts.user_id) > 0)
        .map(counts => {
          const rawUserId = Number(counts.user_id);
          const userLabel = this.getUserStatusDisplayName(counts);
          return {
            label: `${userLabel} (${counts.total_projects || 0} projets)`,
            value: rawUserId,
            user_name: userLabel,
            project_count: counts.total_projects || 0,
          };
        })
        .sort((left, right) => left.label.localeCompare(right.label, 'fr'));

      if (
        this.selectedUserId !== null &&
        !this.usersFilter.some(user => user.value === this.selectedUserId)
      ) {
        this.selectedUserId = null;
      }

      console.log('Utilisateurs disponibles pour le filtre (compteurs globaux):', this.usersFilter);
      return;
    }

    const countsByUser = new Map<number, number>();
    const labelsByUser = new Map<number, string>();

    for (const project of this.projects || []) {
      const rawUserId = Number(project.user_id);
      if (!Number.isFinite(rawUserId) || rawUserId <= 0) {
        continue;
      }

      countsByUser.set(rawUserId, (countsByUser.get(rawUserId) || 0) + 1);

      if (!labelsByUser.has(rawUserId)) {
        const userName = (project.user_name || '').trim();
        const userEmail = (project.user_email || '').trim();
        const fallback = userEmail ? userEmail.split('@')[0] : `Utilisateur ${rawUserId}`;
        labelsByUser.set(rawUserId, userName || fallback);
      }
    }

    this.usersFilter = Array.from(labelsByUser.entries())
      .map(([userId, userLabel]) => ({
        label: `${userLabel} (${countsByUser.get(userId) || 0} projets)`,
        value: userId,
        user_name: userLabel,
        project_count: countsByUser.get(userId) || 0,
      }))
      .sort((left, right) => left.label.localeCompare(right.label, 'fr'));

    if (
      this.selectedUserId !== null &&
      !this.usersFilter.some(user => user.value === this.selectedUserId)
    ) {
      this.selectedUserId = null;
    }

    console.log('Utilisateurs disponibles pour le filtre:', this.usersFilter);
  }

  private loadUserStatusCounts(): void {
    this.projectHistoryService.getProjectUserStatusCounts().subscribe({
      next: (counts) => {
        this.userStatusCounts = this.normalizeUserStatusCounts(counts || []);
        this.loadUsersForFilter();
      },
      error: (error) => {
        console.warn('Compteurs utilisateurs indisponibles, calcul local utilisé:', error);
        this.userStatusCounts = this.buildUserStatusCountsFromProjects(this.getCurrentUserProjects());
      }
    });
  }

  private getCurrentUserProjects(): ProjectHistoryListItem[] {
    if (this.currentUserId === null) {
      return [];
    }

    return (this.projects || []).filter(project => Number(project.user_id) === this.currentUserId);
  }

  private normalizeUserStatusCounts(counts: ProjectUserStatusCounts[]): ProjectUserStatusCounts[] {
    return counts
      .map(count => ({
        ...count,
        user_id: count.user_id ?? null,
        user_name: (count.user_name || '').trim() || null,
        user_email: (count.user_email || '').trim() || null,
        total_projects: Number(count.total_projects) || 0,
        a_traiter_count: Number(count.a_traiter_count) || 0,
        traite_count: Number(count.traite_count) || 0,
      }))
      .filter(count => count.total_projects > 0)
      .sort((left, right) => this.compareUserStatusCounts(left, right));
  }

  private compareUserStatusCounts(left: ProjectUserStatusCounts, right: ProjectUserStatusCounts): number {
    const leftIsCurrentAdmin = this.isCurrentAdminStatusCount(left);
    const rightIsCurrentAdmin = this.isCurrentAdminStatusCount(right);

    if (leftIsCurrentAdmin !== rightIsCurrentAdmin) {
      return leftIsCurrentAdmin ? -1 : 1;
    }

    return this.getUserStatusDisplayName(left).localeCompare(this.getUserStatusDisplayName(right), 'fr');
  }

  private isCurrentAdminStatusCount(counts: ProjectUserStatusCounts): boolean {
    if (!this.isAdmin || this.currentUserId === null) {
      return false;
    }

    const userId = Number(counts.user_id);
    return Number.isFinite(userId) && userId === this.currentUserId;
  }

  private buildUserStatusCountsFromProjects(projects: ProjectHistoryListItem[]): ProjectUserStatusCounts[] {
    const countsByUser = new Map<string, ProjectUserStatusCounts>();

    for (const project of projects || []) {
      const userId = Number(project.user_id);
      const userKey = Number.isFinite(userId) && userId > 0 ? String(userId) : 'unassigned';

      if (!countsByUser.has(userKey)) {
        countsByUser.set(userKey, {
          user_id: userKey === 'unassigned' ? null : userId,
          user_email: project.user_email || null,
          user_name: this.getUserDisplayName(project) || 'Non attribue',
          total_projects: 0,
          a_traiter_count: 0,
          traite_count: 0,
        });
      }

      const userCounts = countsByUser.get(userKey);
      if (!userCounts) {
        continue;
      }

      userCounts.total_projects += 1;

      const normalizedStatus = this.normalizeStatusValue(project.status);
      if (normalizedStatus === 'traite') {
        userCounts.traite_count += 1;
      } else if (normalizedStatus === 'a_traiter') {
        userCounts.a_traiter_count += 1;
      }
    }

    return this.normalizeUserStatusCounts(Array.from(countsByUser.values()));
  }

  private updateProjectSummariesFromProjects(): void {
    this.updateStatusCounters(this.projects);
    this.userStatusCounts = this.buildUserStatusCountsFromProjects(this.getCurrentUserProjects());
  }

  getUserStatusDisplayName(counts: ProjectUserStatusCounts): string {
    const explicitName = (counts.user_name || '').trim();
    if (explicitName) {
      return explicitName;
    }

    const email = (counts.user_email || '').trim();
    if (email) {
      return email.split('@')[0];
    }

    const userId = Number(counts.user_id);
    if (Number.isFinite(userId) && userId > 0) {
      return `Utilisateur ${userId}`;
    }

    return 'Non attribue';
  }

  /**
   * Charge le nombre total de projets
   */
  loadTotalCount() {
    this.projectHistoryService.getTotalProjectsCount().subscribe({
      next: (response) => {
        console.log(`📊 Nombre total de projets dans la base: ${response.count}`);
        this.totalRecords = response.count;
      },
      error: (error) => {
        console.log('ℹ️ Endpoint /count non disponible, utilisation de la méthode diagnostic');
        // Fallback sur le diagnostic si l'endpoint n'existe pas
        this.loadAllProjectsForDiagnostic();
      }
    });
  }

  /**
   * Analyse et affiche toutes les origines uniques présentes dans les données
   */
  analyzeOrigines() {
    if (!this.projects || this.projects.length === 0) {
      console.log('🔍 ANALYSE - Aucun projet à analyser');
      return;
    }

    const origines = new Set<string>();
    console.log('🔍 ANALYSE - Toutes les origines trouvées dans les projets:');

    this.projects.forEach((project, index) => {
      const origine = project.origine || 'Non défini';
      origines.add(origine);
      if (index < 10) { // Afficher les 10 premiers pour debug
        console.log(`  ${index + 1}. ID: ${project.project_id}, Origine: "${origine}"`);
      }
    });

    console.log('🔍 ANALYSE - Origines uniques trouvées:');
    Array.from(origines).sort().forEach(origine => {
      const count = this.projects.filter(p => (p.origine || 'Non défini') === origine).length;
      console.log(`  - "${origine}": ${count} projets`);
    });

    console.log('🔍 ANALYSE - Origines définies dans le filtre:');
    this.origineFilter.forEach(filter => {
      console.log(`  - "${filter.value}": ${filter.label}`);
    });
  }

  /**
   * Charge tous les projets disponibles (pour diagnostic)
   */
  loadAllProjectsForDiagnostic() {
    this.loading = true;
    this.projectHistoryService.getProjectHistory(500, 0).subscribe({
      next: (response: any) => {
        console.log('DIAGNOSTIC - Tous les projets disponibles:', response);
        if (response && typeof response === 'object' && 'items' in response) {
          this.projects = response.items;
          this.filteredProjects = [...this.projects];
          this.updateProjectSummariesFromProjects();
          console.log(`DIAGNOSTIC - Total projets dans la base: ${response.total}`);
          console.log(`DIAGNOSTIC - Projets retournés: ${response.items.length}`);
        } else if (Array.isArray(response)) {
          this.projects = response;
          this.filteredProjects = [...this.projects];
          this.updateProjectSummariesFromProjects();
          console.log(`DIAGNOSTIC - Projets retournés (format liste): ${response.length}`);
        }

        // Analyser les origines après avoir chargé tous les projets
        this.analyzeOrigines();

        this.loading = false;
      },
      error: (error) => {
        console.error('DIAGNOSTIC - Erreur:', error);
        this.loading = false;
      }
    });
  }
  /**
   * Vérifie si des filtres sont actifs
   */
  hasActiveFilters(): boolean {
    return this.searchTerm.trim().length > 0 ||
      this.selectedTypeProjet !== null ||
      this.selectedOrigine !== null ||
      this.selectedStatus !== null ||
      this.selectedEmailValidity !== null ||
      this.selectedCotisationLevel !== null ||
      this.selectedUserId !== null ||
      (this.dateRange !== null && this.dateRange.length === 2);
  }

  getSearchMessage(): any[] {
    const count = this.totalRecords;
    const resultText = count > 1 ? 'résultats' : 'résultat';

    let filters = [];
    if (this.searchTerm.trim()) {
      filters.push(`recherche "${this.searchTerm}"`);
    }
    if (this.selectedTypeProjet) {
      filters.push(`type "${this.getProjectTypeLabel(this.selectedTypeProjet)}"`);
    }
    if (this.selectedOrigine) {
      filters.push(`origine "${this.getOrigineLabel(this.selectedOrigine)}"`);
    }
    if (this.selectedStatus) {
      const selectedStatus = this.statusFilter.find(item => item.value === this.selectedStatus);
      filters.push(`statut "${selectedStatus?.label || this.getStatusLabel(this.selectedStatus)}"`);
    }
    if (this.selectedEmailValidity) {
      filters.push(this.selectedEmailValidity === 'valid' ? 'email "valide"' : 'email "non valide"');
    }
    if (this.selectedCotisationLevel) {
      const selectedCotisation = this.cotisationLevelFilter.find(item => item.value === this.selectedCotisationLevel);
      filters.push(`cotisation "${selectedCotisation?.label || this.selectedCotisationLevel}"`);
    }
    if (this.selectedUserId) {
      const selectedUser = this.usersFilter.find(u => u.value === this.selectedUserId);
      if (selectedUser) {
        const selectedUserLabel = selectedUser.user_name || selectedUser.label;
        filters.push(`utilisateur "${selectedUserLabel}"`);
      }
    }
    if (this.dateRange && this.dateRange.length === 2 && this.dateRange[0] && this.dateRange[1]) {
      const startDate = this.dateRange[0].toLocaleDateString('fr-FR');
      const endDate = this.dateRange[1].toLocaleDateString('fr-FR');
      filters.push(`période du ${startDate} au ${endDate}`);
    }

    // Si on a des filtres actifs, afficher le message
    if (filters.length > 0) {
      const filterText = filters.join(', ');
      return [{
        detail: `${count} ${resultText} pour ${filterText}`
      }];
    }

    return [];
  }

  getSearchMessageDetail(): string {
    const messages = this.getSearchMessage();
    if (!messages.length) {
      return '';
    }

    return messages[0]?.detail || '';
  }

  /**
   * Charge la liste des projets (admin: tous, user: ses projets)
   */
  loadProjects(offset: number = 0, limit: number = this.rows) {
    this.loading = true;
    console.log(`📄 Chargement des projets: offset=${offset}, limit=${limit}`);

    const filters = this.buildServerFilters();
    const loadKey = this.buildProjectLoadKey(offset, limit, filters);
    this.currentProjectLoadKey = loadKey;

    const request$ = this.isAdmin
      ? this.projectHistoryService.getAllProjectsWithFilters(limit, offset, filters)
      : this.projectHistoryService.getProjectHistory(limit, offset, filters);

    request$.subscribe({
      next: (response: PaginatedProjectResponse | any) => {
        this.handleProjectsResponse(response, loadKey);
      },
      error: (error: any) => {
        this.handleProjectsError(error, loadKey);
      }
    });
  }

  /**
   * Gère la réponse des projets
   */
  private handleProjectsResponse(response: any, loadKey: string) {
    if (loadKey !== this.currentProjectLoadKey) {
      return;
    }

    console.log('Données reçues du backend:', response);

    // Vérifie si la réponse a une structure paginée
    if (response && typeof response === 'object' && 'items' in response) {
      // Réponse avec pagination
      this.projects = response.items;
      this.filteredProjects = [...this.projects];
      this.first = response.offset || 0;
      this.rows = response.limit || this.rows;
      console.log(`📄 Projets chargés avec pagination: ${this.projects.length}/${response.total}`);
      this.totalRecords = response.total;
    } else if (Array.isArray(response)) {
      // Réponse simple (liste directe)
      this.projects = response;
      this.filteredProjects = [...this.projects];
      this.totalRecords = response.length;
      console.log(`📄 Projets chargés (liste simple): ${this.projects.length}`);
    } else {
      console.error('Format de réponse inattendu:', response);
      this.projects = [];
      this.filteredProjects = [];
      this.totalRecords = 0;
    }

    this.updateProjectSummariesFromProjects();
    this.loadUsersForFilter();
    this.loadUserStatusCounts();
    this.syncMissingEmailValidationInBackground();
    this.isSearching = this.hasActiveFilters();
    this.persistFiltersState();
    this.lastProjectLoadKey = loadKey;

    this.loading = false;
  }

  /**
   * Gère les erreurs de chargement des projets
   */
  private handleProjectsError(error: any, loadKey: string = this.currentProjectLoadKey) {
    if (loadKey !== this.currentProjectLoadKey) {
      return;
    }

    console.error('Erreur lors du chargement des projets:', error);
    this.messageService.add({
      severity: 'error',
      summary: 'Erreur',
      detail: 'Impossible de charger l\'historique des projets'
    });
    this.loading = false;
  }


  /**
   * Charge un projet et redirige vers le formulaire pré-rempli
   */
  /**
   * Ouvre la transaction associée au projet dans la liste des transactions,
   * pré-filtrée sur son `project_id`.
   */
  openTransaction(project: Pick<ProjectHistoryListItem, 'project_id'>) {
    if (!project?.project_id) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Transaction indisponible',
        detail: 'Aucun identifiant de projet pour ouvrir la transaction associée.'
      });
      return;
    }

    this.persistPaginationState();
    this.router.navigate(['/cac/transactions'], {
      queryParams: { projectId: project.project_id }
    });
  }

  loadProject(project: Pick<ProjectHistoryItem, 'project_id' | 'user_id'>) {
    if (!this.canOpenProject(project)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Accès refusé',
        detail: 'Vous pouvez ouvrir uniquement les projets qui vous sont attribués.'
      });
      return;
    }

    this.loading = true;
    this.persistPaginationState();

    // Récupérer les détails complets du projet
    this.projectHistoryService.getProjectById(project.project_id).subscribe({
      next: (projectDetails) => {
        console.log('📥 Détails du projet récupérés:', projectDetails);

        // Stocker les données du projet dans le localStorage
        localStorage.setItem('projectToLoad', JSON.stringify(projectDetails));
        console.log('💾 Données stockées dans localStorage');

        // Rediriger vers le comparateur avec un paramètre
        console.log('🔀 Navigation vers /cac/comparaison');
        this.router.navigate(['/cac/comparaison'], {
          queryParams: { loadProject: project.project_id }
        });

        this.messageService.add({
          severity: 'info',
          summary: 'Projet chargé',
          detail: `Le projet "${this.getProjectDisplayName(projectDetails)}" a été chargé dans le formulaire`
        });

        this.loading = false;
      },
      error: (error) => {
        console.error('Erreur lors du chargement du projet:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Impossible de charger les détails du projet'
        });
        this.loading = false;
      }
    });
  }

  /**
   * Supprime un projet après confirmation
   */
  private removeProjectsFromState(projectIds: string[]) {
    if (!projectIds.length) {
      return;
    }

    const deletedProjectIds = new Set(projectIds);

    this.projects = this.projects.filter(project => !deletedProjectIds.has(project.project_id));
    this.selectedProjects = this.selectedProjects.filter(project => !deletedProjectIds.has(project.project_id));

    if (this.selectedProjectDetails && deletedProjectIds.has(this.selectedProjectDetails.project_id)) {
      this.closeProjectDetailDialog();
    }

    this.totalRecords = Math.max(0, this.totalRecords - deletedProjectIds.size);
    this.updateProjectSummariesFromProjects();
    this.loadUsersForFilter();
    this.filterProjects();
  }

  private deleteProjectRequest(projectId: string) {
    return this.isAdmin
      ? this.projectHistoryService.deleteProjectAdmin(projectId)
      : this.projectHistoryService.deleteProject(projectId);
  }

  private deleteProjectsRequest(projectIds: string[]) {
    return this.isAdmin
      ? this.projectHistoryService.deleteProjectsAdmin(projectIds)
      : this.projectHistoryService.deleteProjects(projectIds);
  }

  private updateProjectsStatusRequest(projectIds: string[], status: string) {
    return this.isAdmin
      ? this.projectHistoryService.updateProjectsStatusAdmin(projectIds, status)
      : this.projectHistoryService.updateProjectsStatus(projectIds, status);
  }

  private applyStatusToLocalProjects(projectIds: string[], status: string): void {
    if (!projectIds.length) {
      return;
    }

    const idsSet = new Set(projectIds);
    // Valeur réellement stockée côté serveur (statut de lead), pour que la mise à
    // jour optimiste locale affiche la même chose que le rechargement qui suit.
    const normalizedStatus = this.normalizeStatusValue(status);
    const storedStatusByNormalized: Record<string, string> = {
      a_traiter: 'Nouveau',
      traite: 'En cours - Devis Envoyé',
    };
    const targetStatus = storedStatusByNormalized[normalizedStatus] ?? status;

    this.projects = this.projects.map(project => {
      if (!idsSet.has(project.project_id)) {
        return project;
      }

      return {
        ...project,
        status: targetStatus
      };
    });

    this.selectedProjects = this.selectedProjects.map(project => {
      if (!idsSet.has(project.project_id)) {
        return project;
      }

      return {
        ...project,
        status: targetStatus
      };
    });

    if (this.selectedProjectDetails && idsSet.has(this.selectedProjectDetails.project_id)) {
      this.selectedProjectDetails = {
        ...this.selectedProjectDetails,
        status: targetStatus
      };
    }

    this.updateProjectSummariesFromProjects();
    this.loadUsersForFilter();
    this.filterProjects();
  }

  changeSelectedProjectsStatus() {
    if (this.allTableSelected) {
      this.messageService.add({
        severity: 'info',
        summary: 'Sélection complète',
        detail: 'La sélection de tout le tableau est disponible pour l\'export uniquement.'
      });
      return;
    }

    if (!this.canManageStatusActions()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Accès refusé',
        detail: 'Vous devez être connecté pour modifier le statut des projets.'
      });
      return;
    }

    if (!this.selectedProjects.every(project => this.canSelectProject(project))) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Accès refusé',
        detail: 'Vous pouvez modifier le statut uniquement de vos projets.'
      });
      return;
    }

    const projectIds = this.selectedProjects.map(project => project.project_id);

    if (projectIds.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attention',
        detail: 'Veuillez sélectionner au moins un projet'
      });
      return;
    }

    if (!this.selectedBulkStatus) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attention',
        detail: 'Veuillez choisir un statut cible'
      });
      return;
    }

    const targetStatusLabel = this.getStatusLabel(this.selectedBulkStatus);
    const selectionLabel = projectIds.length === 1
      ? `le projet "${this.getProjectDisplayName(this.selectedProjects[0])}"`
      : `${projectIds.length} projets sélectionnés`;

    this.confirmationService.confirm({
      message: `Mettre ${selectionLabel} au statut ${targetStatusLabel} ?`,
      header: 'Confirmer le changement de statut',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Confirmer',
      rejectLabel: 'Annuler',
      accept: () => {
        this.updatingStatus = true;

        this.updateProjectsStatusRequest(projectIds, this.selectedBulkStatus as string).subscribe({
          next: (response: BulkStatusProjectsResponse) => {
            this.applyStatusToLocalProjects(response.updated_project_ids, response.status);

            this.messageService.add({
              severity: response.not_found_project_ids.length > 0 ? 'warn' : 'success',
              summary: response.not_found_project_ids.length > 0 ? 'Mise à jour partielle' : 'Statut mis à jour',
              detail: response.not_found_project_ids.length > 0
                ? `${response.updated_count} projet(s) mis à jour, ${response.not_found_project_ids.length} introuvable(s).`
                : `${response.updated_count} projet(s) mis à jour vers ${this.getStatusLabel(response.status)}.`
            });

            this.selectedBulkStatus = null;
            this.updatingStatus = false;
          },
          error: (error) => {
            console.error('Erreur lors de la mise à jour du statut:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Erreur',
              detail: error?.error?.detail || 'Impossible de mettre à jour le statut des projets sélectionnés'
            });
            this.updatingStatus = false;
          }
        });
      }
    });
  }

  deleteProject(
    project: Pick<ProjectHistoryItem, 'project_id' | 'user_id' | 'raison_sociale' | 'prenom' | 'nom' | 'montant'>,
    options: { closeDetailsOnSuccess?: boolean } = {}
  ) {
    if (!this.canDeleteProject(project)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Accès refusé',
        detail: 'Vous pouvez supprimer uniquement les projets qui vous sont attribués.'
      });
      return;
    }

    this.confirmationService.confirm({
      message: `Êtes-vous sûr de vouloir supprimer le projet "${this.getProjectDisplayName(project)}" ?`,
      header: 'Confirmer la suppression',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Supprimer',
      rejectLabel: 'Annuler',
      accept: () => {
        this.deleteProjectRequest(project.project_id).subscribe({
          next: () => {
            this.removeProjectsFromState([project.project_id]);

            if (options.closeDetailsOnSuccess) {
              this.closeProjectDetailDialog();
            }

            this.messageService.add({
              severity: 'success',
              summary: 'Projet supprimé',
              detail: 'Le projet a été supprimé avec succès'
            });
          },
          error: (error) => {
            console.error('Erreur lors de la suppression:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Erreur',
              detail: 'Impossible de supprimer le projet'
            });
          }
        });
      }
    });
  }

  deleteSelectedProjects() {
    if (this.allTableSelected) {
      this.messageService.add({
        severity: 'info',
        summary: 'Sélection complète',
        detail: 'La sélection de tout le tableau est disponible pour l\'export uniquement.'
      });
      return;
    }

    if (!this.canManageDeleteActions()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Accès refusé',
        detail: 'Vous devez être connecté pour supprimer des projets.'
      });
      return;
    }

    if (!this.selectedProjects.every(project => this.canDeleteProject(project))) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Accès refusé',
        detail: 'Vous pouvez supprimer uniquement vos projets.'
      });
      return;
    }

    const projectIds = this.selectedProjects.map(project => project.project_id);

    if (projectIds.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attention',
        detail: 'Veuillez sélectionner au moins un projet à supprimer'
      });
      return;
    }

    const selectionLabel = projectIds.length === 1
      ? `le projet "${this.getProjectDisplayName(this.selectedProjects[0])}"`
      : `${projectIds.length} projets sélectionnés`;

    this.confirmationService.confirm({
      message: `Êtes-vous sûr de vouloir supprimer ${selectionLabel} ?`,
      header: 'Confirmer la suppression multiple',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Supprimer',
      rejectLabel: 'Annuler',
      accept: () => {
        this.deletingSelection = true;

        this.deleteProjectsRequest(projectIds).subscribe({
          next: (response) => {
            const impactedProjectIds = [
              ...response.deleted_project_ids,
              ...response.not_found_project_ids
            ];

            this.removeProjectsFromState(impactedProjectIds);

            this.messageService.add({
              severity: response.not_found_project_ids.length > 0 ? 'warn' : 'success',
              summary: response.not_found_project_ids.length > 0 ? 'Suppression partielle' : 'Projets supprimés',
              detail: response.not_found_project_ids.length > 0
                ? `${response.deleted_count} projet(s) supprimé(s), ${response.not_found_project_ids.length} introuvable(s) ou déjà supprimé(s).`
                : `${response.deleted_count} projet(s) supprimé(s) avec succès.`
            });

            this.deletingSelection = false;
          },
          error: (error) => {
            console.error('Erreur lors de la suppression multiple:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Erreur',
              detail: error?.error?.detail || 'Impossible de supprimer les projets sélectionnés'
            });
            this.deletingSelection = false;
          }
        });
      }
    });
  }

  /**
   * Duplique un projet
   */
  duplicateProject(project: Pick<ProjectHistoryItem, 'project_id' | 'user_id' | 'raison_sociale' | 'prenom' | 'nom' | 'montant'>) {
    if (!this.canOpenProject(project)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Accès refusé',
        detail: 'Vous pouvez dupliquer uniquement les projets qui vous sont attribués.'
      });
      return;
    }

    this.confirmationService.confirm({
      message: `Voulez-vous dupliquer le projet "${this.getProjectDisplayName(project)}" ?`,
      header: 'Confirmer la duplication',
      icon: 'pi pi-copy',
      acceptLabel: 'Dupliquer',
      rejectLabel: 'Annuler',
      accept: () => {
        this.loading = true;
        this.projectHistoryService.duplicateProject(project.project_id).subscribe({
          next: (response) => {
            this.messageService.add({
              severity: 'success',
              summary: 'Projet dupliqué',
              detail: `Nouveau projet créé: ${response.new_project_id}`
            });

            // Recharger la liste pour afficher le nouveau projet
            this.refresh();
          },
          error: (error) => {
            console.error('Erreur lors de la duplication:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Erreur',
              detail: 'Impossible de dupliquer le projet'
            });
            this.loading = false;
          }
        });
      }
    });
  }

  /**
   * Construit les filtres serveur à partir de l'état du composant.
   */
  private buildServerFilters(): ProjectHistoryFilters {
    const filters: ProjectHistoryFilters = {};

    if (this.searchTerm.trim()) {
      filters.search = this.searchTerm.trim();
    }
    if (this.selectedTypeProjet) {
      filters.type_projet = this.selectedTypeProjet;
    }
    if (this.selectedOrigine) {
      filters.origine = this.selectedOrigine;
    }
    if (this.selectedStatus) {
      filters.status = this.selectedStatus;
    }
    if (this.selectedEmailValidity) {
      filters.email_validity = this.selectedEmailValidity;
    }
    if (this.selectedCotisationLevel) {
      filters.cotisation_level = this.selectedCotisationLevel;
    }
    if (this.selectedUserId) {
      filters.user_id = this.selectedUserId;
    }
if (this.dateRange?.length && this.dateRange[0]) {
  const startDate = new Date(this.dateRange[0]);
  const endDate = new Date(this.dateRange[1] || this.dateRange[0]);

  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);

  filters.start_date = startDate.toISOString().split('T')[0];

  // +1 jour pour inclure complètement la date de fin
  const adjustedEndDate = new Date(endDate);
  adjustedEndDate.setDate(adjustedEndDate.getDate() + 1);

  filters.end_date = adjustedEndDate.toISOString().split('T')[0];
}

    return filters;
  }

  private buildProjectLoadKey(offset: number, limit: number, filters: ProjectHistoryFilters): string {
    return JSON.stringify({
      offset,
      limit,
      filters,
      admin: this.isAdmin
    });
  }

  /**
   * Recharge les projets depuis le serveur avec la pagination et les filtres actifs.
   */
  filterProjects(): void {
    // Un changement de filtre/recherche (ou un rechargement post-action) invalide
    // la sélection courante : sans ce reset, une sélection faite sur un jeu de
    // résultats précédent resterait comptée (ex: « 7 sélectionné(s) » alors qu'un
    // seul projet est visible après recherche).
    this.clearSelection();
    this.isSearching = this.hasActiveFilters();
    this.persistFiltersState();
    this.loadProjects(this.first, this.rows);
  }

  /**
   * Méthode pour le changement de recherche
   */
  onSearchChange(value: string): void {
    this.searchTerm = value;
    this.first = 0;
    this.persistPaginationState();
    this.filterProjects();
  }

  /**
   * Recherche dans les projets
   */
  searchProjects() {
    if (!this.searchTerm.trim()) {
      this.clearSearch();
      return;
    }

    if (this.searchTerm.length < 2) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Recherche',
        detail: 'Veuillez saisir au moins 2 caractères'
      });
      return;
    }

    this.first = 0;
    this.persistPaginationState();
    this.filterProjects();
  }

  /**
   * Réinitialise la recherche et tous les filtres
   */
  clearSearch() {
    console.log('🔄 Clear search - retour à tous les projets');
    this.searchTerm = '';
    this.selectedTypeProjet = null;
    this.selectedOrigine = null;
    this.selectedStatus = null;
    this.selectedEmailValidity = null;
    this.selectedCotisationLevel = null;
    this.selectedUserId = null;
    this.dateRange = null;
    this.selectedStartDate = null;
    this.selectedEndDate = null;
    this.first = 0; // Reset à la première page
    this.persistPaginationState();
    // Réappliquer le filtrage (qui va montrer tous les projets)
    this.filterProjects();
  }

  /**
   * Gère le changement de plage de dates
   */
  onDateRangeChange() {
    console.log('📅 Changement de plage de dates:', this.dateRange);

    if (this.dateRange && this.dateRange.length > 0) {
      const start = this.dateRange[0];
      const end = this.dateRange[1] || start; // If no end date, use start date for a single-day filter

      if (start && start instanceof Date && !isNaN(start.getTime()) && 
          end && end instanceof Date && !isNaN(end.getTime())) {
        console.log(`📅 Du ${start.toLocaleDateString('fr-FR')} au ${end.toLocaleDateString('fr-FR')}`);
        this.filterProjects();
      } else {
        console.warn('📅 Dates invalides détectées');
      }
    } else if (!this.dateRange || this.dateRange.length === 0) {
      console.log('📅 Plage de dates effacée');
      this.filterProjects();
    }
  }

  /**
   * Gestion de la pagination (côté client)
   */
  onPageChange(event: any) {
    console.log('📄 Changement de page:', event);
    const nextFirst = Number(event.first || 0);
    const nextRows = Number(event.rows || this.rows || 10);
    const nextFilters = this.buildServerFilters();
    const nextLoadKey = this.buildProjectLoadKey(nextFirst, nextRows, nextFilters);

    if (this.loading && nextLoadKey === this.currentProjectLoadKey) {
      return;
    }

    if (!this.loading && nextLoadKey === this.lastProjectLoadKey) {
      return;
    }

    this.first = nextFirst;
    this.rows = nextRows;
    this.persistPaginationState();
    this.loadProjects(this.first, this.rows);
  }


  /**
   * Actualise la liste
   */
  refresh() {
    console.log('🔄 Actualisation de la liste');
    // Recharger tous les projets depuis le serveur
    this.loadTotalCount(); // Recharger le total
    this.loadProjects(this.first, this.rows);
  }

  /**
   * Naviguer vers le formulaire de comparaison pour créer un nouveau projet
   */
  createNewProject() {
    // S'assurer qu'aucun projet n'est en cours de chargement
    localStorage.removeItem('projectToLoad');
    console.log('➕ Création d\'un nouveau projet - Navigation vers /cac/comparaison');
    this.router.navigate(['/cac/comparaison']);
  }

  /**
   * Retourne un nom d'affichage pour le projet
   */
  getProjectDisplayName(project: Pick<ProjectHistoryItem, 'raison_sociale' | 'prenom' | 'nom' | 'montant'>): string {
    if (project.raison_sociale) {
      return `${project.raison_sociale} - ${this.formatAmount(project.montant || 0)}€`;
    }
    return `${project.prenom || ''} ${project.nom || ''} - ${this.formatAmount(project.montant || 0)}€`;
  }

  /**
   * Formate un montant
   */
  formatAmount(amount: number | string | null | undefined): string {
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount)) {
      return '0,00';
    }

    return numericAmount.toLocaleString('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  /**
   * Formate un montant de cotisation au format 57.20 et 28,826.77.
   */
  formatCotisationAmount(amount: number | string | null | undefined): string {
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount)) {
      return '0.00';
    }

    return numericAmount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  /**
   * Formate une date
   */
  formatDate(dateString: string): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  /**
   * Formate une date courte
   */
  formatDateShort(dateString: string): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  /**
   * Formate une heure courte
   */
  formatTimeShort(dateString: string): string {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  private toNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    if (typeof value === 'string') {
      const cleaned = value.replace(/\s+/g, '').replace(',', '.');
      const parsed = Number(cleaned);
      return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
  }

  private parseAdditionalLoans(raw: ProjectHistoryListItem['prets_additionnels']): any[] {
    if (!raw) {
      return [];
    }

    if (Array.isArray(raw)) {
      return raw;
    }

    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }

    return [];
  }

  private resolveAdditionalLoanAmount(loan: any): number | null {
    return this.toNumber(loan?.capitalRestantDu ?? loan?.montant ?? loan?.capital ?? loan?.crd);
  }

  private resolveAdditionalLoanDuration(loan: any): number | null {
    return this.toNumber(loan?.dureeRestante ?? loan?.duree ?? loan?.mois);
  }

  private resolveAdditionalLoanRate(loan: any): number | null {
    return this.toNumber(loan?.tauxPret ?? loan?.tauxInteret ?? loan?.taux);
  }

  getProjectLoanLines(project: ProjectHistoryListItem): ProjectLoanLine[] {
    const lines: ProjectLoanLine[] = [
      {
        amount: this.toNumber(project.montant),
        duration: this.toNumber(project.duree),
        rate: this.toNumber(project.taux),
      }
    ];

    const additionalLoans = this.parseAdditionalLoans(project.prets_additionnels);
    for (const loan of additionalLoans) {
      const line = {
        amount: this.resolveAdditionalLoanAmount(loan),
        duration: this.resolveAdditionalLoanDuration(loan),
        rate: this.resolveAdditionalLoanRate(loan),
      };

      if (line.amount === null && line.duration === null && line.rate === null) {
        continue;
      }

      lines.push(line);
    }

    if (lines.length > 1) {
      return lines.map((line, index) => ({
        ...line,
        label: `Prêt ${index + 1}`,
      }));
    }

    return lines;
  }

  /**
   * Retourne la couleur du tag selon le type de projet
   */
  getProjectTypeColor(typeProjet: string): 'success' | 'info' | 'warning' | 'danger' | 'secondary' {
    switch (typeProjet) {
      case 'residence_principale': return 'success';
      case 'residence_secondaire': return 'info';
      case 'investissement_locatif': return 'warning';
      case 'TER': return 'secondary';
      case 'CONST': return 'info';
      default: return 'secondary';
    }
  }

  /**
   * Formate le libellé du type de projet
   */
  getProjectTypeLabel(typeProjet: string): string {
    switch (typeProjet) {
      case 'residence_principale': return 'Résidence principale';
      case 'residence_secondaire': return 'Résidence secondaire';
      case 'investissement_locatif': return 'Investissement locatif';
      case 'TER': return 'Terrain';
      case 'CONST': return 'Construction';
      default: return typeProjet || 'Non spécifié';
    }
  }

  /**
   * Retourne le libellé à afficher pour le tag "Nature du projet".
   * Si le type est absent, on utilise le nom de projet comme fallback.
   */
  getProjectTypeDisplay(project: Pick<ProjectHistoryListItem, 'type_projet' | 'nom_projet'>): string {
    const rawType = (project.type_projet || '').trim();
    if (rawType) {
      return this.getProjectTypeLabel(rawType);
    }

    return (project.nom_projet || '').trim();
  }

  /**
   * `status` porte le statut de lead (identique à la fiche contact). Cette
   * fonction renvoie le libellé de lead à afficher, en convertissant au passage
   * les anciennes valeurs techniques encore présentes en base (non migrées) :
   *   « en_cours » / « a_traiter »  → « Nouveau »
   *   « traite »                    → « En cours - Devis Envoyé »
   */
  private leadStatusLabel(status?: string): string {
    const raw = (status || '').trim();
    if (!raw) {
      return '';
    }
    const key = raw
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

    const legacy: Record<string, string> = {
      'en cours': 'Nouveau',
      'a traiter': 'Nouveau',
      nouveau: 'Nouveau',
      traite: 'En cours - Devis Envoyé',
      'en cours devis envoye': 'En cours - Devis Envoyé',
      'en cours qs envoye': 'En cours - QS Envoyé',
      'a recontacter': 'A recontacter',
      nrp: 'NRP',
      'non qualifie refus': 'Non qualifié - Refus',
      'perdu trop cher': 'Perdu - Trop Cher',
      'perdu refus assureur': 'Perdu - Refus Assureur',
      'converti en transaction': 'Converti en transaction',
    };
    return legacy[key] ?? raw;
  }

  /**
   * Retourne la couleur du tag selon le statut de lead (mêmes teintes que
   * l'écran Contacts).
   */
  getStatusColor(status?: string): 'success' | 'danger' | 'info' | 'warning' | 'secondary' {
    switch (this.leadStatusLabel(status)) {
      case 'Converti en transaction':
        return 'success';
      case 'En cours - Devis Envoyé':
      case 'En cours - QS Envoyé':
        return 'info';
      case 'A recontacter':
        return 'warning';
      case 'NRP':
      case 'Non qualifié - Refus':
      case 'Perdu - Trop Cher':
      case 'Perdu - Refus Assureur':
        return 'danger';
      default:
        return 'secondary';
    }
  }

  /**
   * Couleur du tag "Phase" du pipeline — mêmes couleurs que l'écran Transactions.
   */
  getPhaseSeverity(phase?: string | null): 'success' | 'danger' | 'info' | 'warning' | 'secondary' {
    switch (phase) {
      case 'Fermé gagné':                   return 'success';
      case 'Fermé perdu':                   return 'danger';
      case 'Devis envoyé':
      case 'Questionnaire médical envoyé':  return 'info';
      case 'Relance & objections en cours': return 'warning';
      default:                              return 'secondary';
    }
  }

  /**
   * Libellé du résultat d'appel (Transaction.resultat_appel) :
   * OK / NRP, null = jamais appelé → « Nouveau ».
   */
  getResultatAppelLabel(resultat?: string | null): string {
    return (resultat || '').trim() || 'Nouveau';
  }

  resultatAppelOptions = ['OK', 'NRP'];
  savingResultatAppelProjectId: string | null = null;

  /** Édition manuelle du résultat d'appel (écrit Transaction.resultat_appel,
   *  partagé avec l'agenda et la table transactions). */
  onResultatAppelChange(project: ProjectHistoryItem, value: string | null): void {
    if (!project.project_id || this.savingResultatAppelProjectId) return;
    const previous = project.resultat_appel ?? null;
    if (value === previous) return;
    this.savingResultatAppelProjectId = project.project_id;
    this.transactionService.updateResultatAppelByProject(project.project_id, value).subscribe({
      next: (res) => {
        this.savingResultatAppelProjectId = null;
        project.resultat_appel = res.resultat_appel;
        this.messageService.add({
          severity: 'success', summary: "Résultat d'appel",
          detail: this.getResultatAppelLabel(res.resultat_appel), life: 2000,
        });
      },
      error: (err) => {
        this.savingResultatAppelProjectId = null;
        project.resultat_appel = previous;
        this.messageService.add({
          severity: 'error', summary: 'Erreur',
          detail: "Changement du résultat d'appel impossible (aucune transaction liée ?)",
        });
        console.error(err);
      },
    });
  }

  getResultatAppelSeverity(resultat?: string | null): 'success' | 'danger' | 'secondary' {
    switch ((resultat || '').trim().toUpperCase()) {
      case 'OK':  return 'success';
      case 'NRP': return 'danger';
      default:    return 'secondary';
    }
  }

  /**
   * Retourne le libellé lisible du statut
   */
  getStatusLabel(status?: string): string {
    return this.leadStatusLabel(status) || 'Non défini';
  }

  /**
   * Retourne la classe CSS du statut pour un style visuel explicite
   */
  getStatusClass(status?: string): string {
    switch (this.getStatusColor(status)) {
      case 'success': return 'status-success';
      case 'info':    return 'status-info';
      case 'warning': return 'status-warning';
      case 'danger':  return 'status-danger';
      default:        return 'status-default';
    }
  }

  /**
   * Retourne la couleur selon le type de souscripteur
   */
  getSouscripteurColor(type: string): 'info' | 'secondary' {
    return type === 'Personne morale' ? 'info' : 'secondary';
  }

  // ...existing code...

  /**
   * Retourne la couleur selon l'origine du projet
   */
  getOrigineColor(origine: string): 'success' | 'info' | 'warning' | 'danger' | 'secondary' {
    switch (this.normalizeOrigineValue(origine)) {
      case 'FOURNISSEUR_DE_LEAD': return 'success';
      case 'RDV_LBC': return 'info';
      case 'RDV_DIRECT': return 'info';
      case 'TELEPHONE': return 'warning';
      case 'MultiEquipement': return 'warning';
      case 'INTERNET': return 'success';
      case 'COURTIER': return 'info';
      case 'PARRAINAGE': return 'warning';
      case 'Doublon': return 'danger';
      default: return 'secondary';
    }
  }

  /**
   * Retourne le libellé de l'origine
   */
  getOrigineLabel(origine: string): string {
    const normalizedOrigine = this.normalizeOrigineValue(origine);
    const option = this.origineFilter.find(item => item.value === normalizedOrigine);
    if (option) {
      return option.label;
    }
    return origine || 'Non spécifié';
  }

  private normalizeOrigineValue(origine: string | null | undefined): string {
    if (!origine) {
      return '';
    }

    const normalized = origine
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toUpperCase();

    const aliases: Record<string, string> = {
      COMPARADISE: 'FOURNISSEUR_DE_LEAD',
      'LEAD FOURNISSEUR': 'FOURNISSEUR_DE_LEAD',
      'FOURNISSEUR DE LEAD': 'FOURNISSEUR_DE_LEAD',
      'LEAD ASSUREUR': 'RDV_LBC',
      'APPEL ENTANT': 'TELEPHONE',
      TELEPROSPECTION: 'RDV_DIRECT',
      'SITE WEB': 'INTERNET',
      APPORTEUR: 'COURTIER',
      MULTIEQUIPEMENT: 'MultiEquipement',
      'RDV LBC': 'RDV_LBC',
      'RDV DIRECT': 'RDV_DIRECT',
      'RDV AUTRES': 'RDV_DIRECT',
      TELEPHONE: 'TELEPHONE',
      'APPEL ENTRANT': 'TELEPHONE',
      DIRECT: 'MultiEquipement',
      'PARTENAIRE AUTRES': 'MultiEquipement',
      INTERNET: 'INTERNET',
      'SITE WEB BROKINS': 'INTERNET',
      COURTIER: 'COURTIER',
      'PARTENAIRE BANQUE': 'COURTIER',
      'FORCE DE VENTE': 'PARRAINAGE',
      PARRAINAGE: 'PARRAINAGE',
      DOUBLON: 'Doublon'
    };

    return aliases[normalized] ?? origine.trim();
  }

  private normalizeStatusValue(status: string | null | undefined): string {
    if (!status) {
      return '';
    }

    const normalized = status
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

    // `status` porte désormais le statut de lead (copie de Contact.statut_du_lead).
    // Équivalences validées avec l'ancien référentiel :
    //   « En cours - Devis Envoyé » == ancien « traité »
    //   « Nouveau »                 == ancien « à traiter »
    // Les anciennes valeurs restent reconnues (données pas encore toutes migrées).
    const aliases: Record<string, string> = {
      nouveau: 'a_traiter',
      'en cours devis envoye': 'traite',
      traite: 'traite',
      'en cours': 'a_traiter',
      'a traiter': 'a_traiter',
      'pas interesse de suite': 'pas_interesse',
      'pas interesse': 'pas_interesse',
      'a supprimer': 'a_supprimer',
      'trop cher': 'trop_cher',
      medical: 'medical',
      'refus sante': 'refus_sante'
    };

    return aliases[normalized] ?? normalized.replace(/\s+/g, '_');
  }

  private updateStatusCounters(projects: ProjectHistoryListItem[]): void {
    let aTraiterCount = 0;
    let traiteCount = 0;

    for (const project of projects) {
      const normalizedStatus = this.normalizeStatusValue(project.status);
      if (normalizedStatus === 'traite') {
        traiteCount += 1;
        continue;
      }

      if (normalizedStatus === 'a_traiter') {
        aTraiterCount += 1;
      }
    }

    this.totalATraiter = aTraiterCount;
    this.totalTraite = traiteCount;
  }

  // ...existing code...
  /**
   * Gestion de la saisie dans la recherche
   */
  onSearchInput() {
    if (!this.searchTerm.trim() && this.isSearching) {
      this.clearSearch();
    }
  }


  /**
   * Retourne l'email de l'utilisateur assigné au projet
   */
  getUserEmail(project: ProjectHistoryListItem): string {
    return project.user_email || '';
  }

  /**
   * Retourne le nom utilisateur à afficher dans le tableau.
   */
  getUserDisplayName(project: ProjectHistoryListItem): string {
    const explicitName = (project.user_name || '').trim();
    if (explicitName) {
      return explicitName;
    }

    const email = (project.user_email || '').trim();
    if (email) {
      return email.split('@')[0];
    }

    const userId = Number(project.user_id);
    if (Number.isFinite(userId) && userId > 0) {
      return `Utilisateur ${userId}`;
    }

    return '';
  }

  getClientBadgeLabel(project: ProjectHistoryListItem): string {
    const ownerName = this.getUserDisplayName(project);
    if (ownerName) {
      return ownerName;
    }

    return 'Non attribue';
  }

  getClientBadgeSeverity(project: ProjectHistoryListItem): 'info' | 'warn' {
    return this.getUserDisplayName(project) ? 'info' : 'warn';
  }

  private isProjectOwnedByCurrentUser(project?: Pick<ProjectHistoryItem, 'user_id'> | null): boolean {
    if (!project || this.currentUserId === null) {
      return false;
    }

    const projectOwnerId = Number(project.user_id);
    return Number.isFinite(projectOwnerId) && projectOwnerId > 0 && projectOwnerId === this.currentUserId;
  }

  canOpenProject(project?: Pick<ProjectHistoryItem, 'user_id'> | null): boolean {
    return this.isAdmin || this.isProjectOwnedByCurrentUser(project);
  }

  canDeleteProject(project?: Pick<ProjectHistoryItem, 'user_id'> | null): boolean {
    return this.isAdmin || this.isProjectOwnedByCurrentUser(project);
  }

  canSelectProject(project?: Pick<ProjectHistoryItem, 'user_id'> | null): boolean {
    return this.isAdmin || this.isProjectOwnedByCurrentUser(project);
  }

  hasSelectableVisibleProjects(): boolean {
    return this.filteredProjects.some(project => this.canSelectProject(project));
  }

  canAssignSelectedProjects(): boolean {
    return this.isAdmin || this.currentUserId !== null;
  }

  canManageStatusActions(): boolean {
    return this.isAdmin || this.currentUserId !== null;
  }

  canManageDeleteActions(): boolean {
    return this.isAdmin || this.currentUserId !== null;
  }

  canManageSelectionActions(): boolean {
    return this.isAdmin;
  }

  canSendBulkEmails(): boolean {
    return this.isAdmin || this.currentUserId !== null;
  }

  /**
   * Options du menu d'actions unifié (Importer, Exporter, Attribuer, Changer statut, Supprimer...)
   */
  get actionMenuOptions(): { label: string; value: string }[] {
    const options: { label: string; value: string }[] = [];

    if (this.isAdmin) {
      options.push({ label: 'Importer des projets (CSV)', value: 'import_csv' });
      options.push({ label: 'Exporter en Excel', value: 'export_excel' });
      options.push({ label: 'Supprimer via Excel', value: 'delete_excel' });
    }

    if (this.canAssignSelectedProjects()) {
      options.push({ label: 'Attribuer la sélection', value: 'assign' });
    }

    if (this.canManageStatusActions()) {
      options.push({ label: 'Changer le statut de la sélection', value: 'change_status' });
    }

    if (this.canSendBulkEmails()) {
      options.push({ label: 'Envoyer une demande d\'information', value: 'send_info_email' });
      options.push({ label: 'Envoyer un email d\'économies', value: 'send_savings_email' });
      options.push({ label: 'Renvoyer SMS + Devis', value: 'send_sms_devis' });
    }

    if (this.canManageDeleteActions()) {
      options.push({ label: 'Supprimer la sélection', value: 'delete_selection' });
    }

    return options;
  }

  /**
   * Réinitialise les paramètres dépendants lors du changement d'action.
   */
  onActionMenuChange(): void {
    if (this.selectedAction !== 'change_status') {
      this.selectedBulkStatus = null;
    }
  }

  /**
   * Exécute l'action sélectionnée dans le menu d'actions unifié.
   */
  executeSelectedAction(): void {
    switch (this.selectedAction) {
      case 'import_csv':
        this.triggerCsvImport();
        break;
      case 'export_excel':
        this.exportHistoryToExcel();
        break;
      case 'delete_excel':
        this.triggerMobileDeleteExcelImport();
        break;
      case 'assign':
        this.openAssignmentDialog();
        break;
      case 'change_status':
        this.changeSelectedProjectsStatus();
        break;
      case 'send_info_email':
        this.sendBulkInformationRequest();
        break;
      case 'send_savings_email':
        this.sendBulkSavingsEmailRequest();
        break;
      case 'send_sms_devis':
        this.sendBulkSmsAndDevisRequest();
        break;
      case 'delete_selection':
        this.deleteSelectedProjects();
        break;
    }
  }

  /**
   * Indique si l'action sélectionnée est en cours d'exécution.
   */
  isExecutingSelectedAction(): boolean {
    switch (this.selectedAction) {
      case 'import_csv':
        return this.importingCsv;
      case 'export_excel':
        return this.exportingExcel;
      case 'delete_excel':
        return this.deletingByMobileExcel;
      case 'assign':
        return this.assigningProjects;
      case 'change_status':
        return this.updatingStatus;
      case 'send_info_email':
        return this.sendingBulkInfoEmail;
      case 'send_savings_email':
        return this.sendingBulkSavingsEmail;
      case 'send_sms_devis':
        return this.sendingBulkDevisResend || this.sendingBulkSmsRappel;
      case 'delete_selection':
        return this.deletingSelection;
      default:
        return false;
    }
  }

  /**
   * Indique si le bouton "Exécuter" doit être actif.
   */
  canExecuteSelectedAction(): boolean {
    if (!this.selectedAction || this.isExecutingSelectedAction()) {
      return false;
    }

    if (this.selectedAction === 'change_status' && !this.selectedBulkStatus) {
      return false;
    }

    return true;
  }

  isCurrentAdminAssignee(user: { id?: number | string } | null | undefined): boolean {
    if (!this.isAdmin || this.currentUserId === null || !user) {
      return false;
    }

    const userId = Number(user.id);
    return Number.isFinite(userId) && userId === this.currentUserId;
  }

  getAssigneeDisplayLabel(user: { id?: number | string; full_name?: string; email?: string } | null | undefined): string {
    if (!user) {
      return '';
    }

    if (this.isCurrentAdminAssignee(user)) {
      return 'Moi (Admin)';
    }

    const fullName = (user.full_name || '').trim();
    if (fullName) {
      return fullName;
    }

    const email = (user.email || '').trim();
    if (email) {
      return email;
    }

    const userId = Number(user.id);
    return Number.isFinite(userId) ? `Utilisateur ${userId}` : 'Utilisateur';
  }

  /**
   * Évite d'afficher un nom de projet identique au type de projet.
   */
  shouldShowProjectName(project: ProjectHistoryListItem): boolean {
    const projectName = (project.nom_projet || '').trim();
    if (!projectName) {
      return false;
    }

    if (!(project.type_projet || '').trim()) {
      return false;
    }

    const rawType = (project.type_projet || '').trim();
    const typeLabel = this.getProjectTypeLabel(rawType).trim();

    const normalize = (value: string) => value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

    return normalize(projectName) !== normalize(rawType) && normalize(projectName) !== normalize(typeLabel);
  }

  /**
   * Retourne le label lisible de la banque
   */
  getBanqueLabel(banqueValue: string): string {
    if (!banqueValue) return '-';
    const found = BANQUES_LIST.find(b => b.value === banqueValue);
    if (found) return found.label;
    // Fallback: formater le code brut en gérant les accents
    return banqueValue
      .replace(/_/g, ' ')
      .replace(/(?:^|\s|-)\S/g, c => c.toUpperCase());
  }

  /**
   * Retourne les initiales du client
   */
  getClientInitials(project: ProjectHistoryListItem): string {
    if (project.raison_sociale) {
      return project.raison_sociale.substring(0, 2).toUpperCase();
    }
    const p = project.prenom?.charAt(0) ?? '';
    const n = project.nom?.charAt(0) ?? '';
    if (p || n) {
      return (p + n).toUpperCase();
    }
    if (project.email) {
      return project.email.substring(0, 2).toUpperCase();
    }
    return '?';
  }

  /**
   * Compte les projets créés ce mois
   */
  getActiveProjectsCount(): number {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();

    return this.projects.filter(project => {
      const projectDate = new Date(project.created_at);
      return projectDate.getMonth() === currentMonth &&
        projectDate.getFullYear() === currentYear;
    }).length;
  }

  /**
   * Compte les projets avec co-emprunteur
   */
  getCoEmprunteurCount(): number {
    return this.projects.filter(project => project.est_co_emprunteur).length;
  }

  /**
   * Gestion du changement de filtre par type de projet
   */
  onTypeProjetChange() {
    console.log('🔽 Changement de recherche type projet:', this.selectedTypeProjet);
    // Reset pagination lors du changement de recherche
    this.first = 0;
    this.persistPaginationState();
    // Appliquer la recherche côté client
    this.filterProjects();
  }

  /**
   * Gestion du changement de filtre par origine
   */
  onOrigineChange() {
    console.log('🔽 Changement de recherche origine:', this.selectedOrigine);
    // Reset pagination lors du changement de recherche
    this.first = 0;
    this.persistPaginationState();
    // Appliquer la recherche côté client
    this.filterProjects();
  }

  /**
   * Gestion du changement de filtre par statut
   */
  onStatusChange() {
    console.log('🔽 Changement de recherche statut:', this.selectedStatus);
    // Reset pagination lors du changement de recherche
    this.first = 0;
    this.persistPaginationState();
    // Appliquer la recherche côté client
    this.filterProjects();
  }

  /**
   * Gestion du changement de filtre par validité d'email
   */
  onEmailValidityChange() {
    console.log('🔽 Changement de recherche validité email:', this.selectedEmailValidity);
    this.first = 0;
    this.persistPaginationState();
    if (this.selectedEmailValidity && this.getProjectsMissingEmailValidation().length > 0) {
      this.syncMissingEmailValidationInBackground();
    }
    this.filterProjects();
  }

  /**
   * Gestion du changement de filtre par niveau de cotisation mensuelle
   */
  onCotisationLevelChange() {
    console.log('🔽 Changement de recherche cotisation:', this.selectedCotisationLevel);
    this.first = 0;
    this.persistPaginationState();
    this.filterProjects();
  }

  /**
   * Gestion du changement de filtre par utilisateur
   */
  onUserChange() {
    console.log('🔽 Changement de recherche utilisateur:', this.selectedUserId);
    // Reset pagination lors du changement de recherche
    this.first = 0;
    this.persistPaginationState();
    // Appliquer la recherche côté client
    this.filterProjects();
  }
  // Ajoutez cette méthode dans votre composant :



  /**
   * Nom d'affichage à partir des données complètes d'un projet
   */
  private getProjectDisplayNameFromData(project: any): string {
    if (project.raison_sociale) {
      return `${project.raison_sociale} - ${this.formatAmount(project.montant || 0)}€`;
    }
    return `${project.prenom || ''} ${project.nom || ''} - ${this.formatAmount(project.montant || 0)}€`;
  }
  viewProjectDetails(project: ProjectHistoryListItem) {
    if (!this.canOpenProject(project)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Accès refusé',
        detail: 'Vous pouvez ouvrir uniquement les projets qui vous sont attribués.'
      });
      return;
    }

    console.log('Voir détails du projet:', project);
    this.loadingDetails = true;
    this.showProjectDetailDialog = true;

    this.projectHistoryService.getProjectById(project.project_id).subscribe({
      next: (details) => {
        this.selectedProjectDetails = details;
        this.loadingDetails = false;
      },
      error: (error) => {
        console.error('Erreur lors du chargement des détails du projet:', error);
        this.loadingDetails = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Impossible de charger les détails complets du projet'
        });
        this.closeProjectDetailDialog();
      }
    });
  }

  formatDetailLabel(key: string): string {
    if (this.detailLabelOverrides[key]) {
      return this.detailLabelOverrides[key];
    }

    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  }

  formatDetailValue(key: string, value: any): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    if (typeof value === 'boolean') {
      return value ? 'Oui' : 'Non';
    }

    if (typeof value === 'number') {
      if (['montant', 'montant_echeance_palier'].includes(key)) {
        return `${this.formatAmount(value)} €`;
      }
      if (['en_cours_total_assure', 'co_emprunteur_en_cours_total_assure'].includes(key)) {
        return `${value.toLocaleString('fr-FR')} €`;
      }
      if (key === 'taux') {
        return `${value.toLocaleString('fr-FR')} %`;
      }
      if (key === 'duree' || key === 'duree_palier') {
        return `${value.toLocaleString('fr-FR')} mois`;
      }
      return value.toLocaleString('fr-FR');
    }

    if (typeof value === 'string') {
      if (['created_at', 'date_naissance', 'date_effective', 'co_emprunteur_date_naissance'].includes(key)) {
        return this.formatDate(value);
      }
      if (key === 'banque') {
        return this.getBanqueLabel(value);
      }
      if (key === 'type_projet') {
        return this.getProjectTypeLabel(value);
      }
      if (key === 'origine') {
        return this.getOrigineLabel(value);
      }
    }

    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }

    return String(value);
  }

  getAllProjectDetailsEntries(): Array<{ key: string; value: any }> {
    if (!this.selectedProjectDetails) {
      return [];
    }

    return Object.entries(this.selectedProjectDetails)
      .filter(([_, value]) => value !== null && value !== undefined && value !== '')
      .sort(([keyA], [keyB]) => {
        const orderA = this.detailFieldOrder.indexOf(keyA);
        const orderB = this.detailFieldOrder.indexOf(keyB);

        if (orderA !== -1 || orderB !== -1) {
          if (orderA === -1) return 1;
          if (orderB === -1) return -1;
          return orderA - orderB;
        }

        return keyA.localeCompare(keyB);
      })
      .map(([key, value]) => ({ key, value }));
  }

  /**
   * Fermer le dialog de détails
   */
  closeProjectDetailDialog() {
    this.showProjectDetailDialog = false;
    this.selectedProjectDetails = null;
  }

  /**
   * Charger le projet depuis le dialog de détails
   */
  loadProjectFromDialog() {
    if (this.selectedProjectDetails && this.canOpenProject(this.selectedProjectDetails)) {
      this.loadProject(this.selectedProjectDetails);
      this.closeProjectDetailDialog();
      return;
    }

    if (this.selectedProjectDetails) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Accès refusé',
        detail: 'Vous pouvez ouvrir uniquement les projets qui vous sont attribués.'
      });
    }
  }

  /**
   * Dupliquer le projet depuis le dialog de détails
   */
  duplicateProjectFromDialog() {
    if (this.selectedProjectDetails && this.canOpenProject(this.selectedProjectDetails)) {
      this.duplicateProject(this.selectedProjectDetails);
      this.closeProjectDetailDialog();
      return;
    }

    if (this.selectedProjectDetails) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Accès refusé',
        detail: 'Vous pouvez dupliquer uniquement les projets qui vous sont attribués.'
      });
    }
  }

  /**
   * Supprimer le projet depuis le dialog de détails
   */
  deleteProjectFromDialog() {
    if (this.selectedProjectDetails && this.canDeleteProject(this.selectedProjectDetails)) {
      this.deleteProject(this.selectedProjectDetails, { closeDetailsOnSuccess: true });
      return;
    }

    if (this.selectedProjectDetails) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Accès refusé',
        detail: 'Vous pouvez supprimer uniquement les projets qui vous sont attribués.'
      });
    }
  }

  /**
   * Voir le projet source (pour les doublons)
   */
  viewSourceProject(sourceProjectId: string) {
    if (!sourceProjectId) return;

    this.loading = true;

    // Chercher le projet source dans la liste actuelle
    const sourceProject = this.projects.find(p => p.project_id === sourceProjectId);

    if (sourceProject) {
      this.loading = false;
      this.viewProjectDetails(sourceProject);
    } else {
      // Si pas trouvé localement, afficher un message
      this.loading = false;
      this.messageService.add({
        severity: 'info',
        summary: 'Projet Source',
        detail: `Projet source ${sourceProjectId} non trouvé dans la liste actuelle`,
        life: 5000
      });
    }
  }

  /**
   * Ouvre le dialog d'attribution de projets
   */
  openAssignmentDialog() {
    if (this.allTableSelected) {
      this.messageService.add({
        severity: 'info',
        summary: 'Sélection complète',
        detail: 'La sélection de tout le tableau est disponible pour l\'export uniquement.'
      });
      return;
    }

    if (!this.canAssignSelectedProjects()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Accès refusé',
        detail: 'Vous devez être connecté pour attribuer des projets.'
      });
      return;
    }

    if (this.selectedProjects.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attention',
        detail: 'Veuillez sélectionner au moins un projet à attribuer'
      });
      return;
    }

    if (!this.selectedProjects.every(project => this.canSelectProject(project))) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Accès refusé',
        detail: 'Vous pouvez attribuer uniquement les projets qui vous appartiennent.'
      });
      return;
    }

    this.loadActiveUsers();
    this.showAssignmentDialog = true;
  }

  /**
   * Charge la liste des utilisateurs actifs
   */
  loadActiveUsers() {
    this.projectHistoryService.getActiveUsers().subscribe({
      next: (users) => {
        const normalizedUsers = Array.isArray(users) ? [...users] : [];
        const currentAdminAssignee = this.getCurrentAdminAssignee();

        if (currentAdminAssignee) {
          const alreadyPresent = normalizedUsers.some(user => Number(user?.id) === currentAdminAssignee.id);

          if (!alreadyPresent) {
            normalizedUsers.unshift(currentAdminAssignee);
          }
        }

        this.activeUsers = normalizedUsers;

        if (currentAdminAssignee && !this.selectedTargetUser) {
          const selfUser = this.activeUsers.find(user => Number(user?.id) === currentAdminAssignee.id);
          if (selfUser) {
            this.selectedTargetUser = selfUser;
          }
        }

        console.log('Utilisateurs actifs chargés:', users);
      },
      error: (error) => {
        console.error('Erreur lors du chargement des utilisateurs actifs:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Impossible de charger la liste des utilisateurs'
        });
      }
    });
  }

  /**
   * Attribue les projets sélectionnés
   */
  assignProjects() {
    if (!this.selectedTargetUser) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attention',
        detail: 'Veuillez sélectionner un utilisateur destinataire'
      });
      return;
    }

    this.assigningProjects = true;

    const assignmentData = {
      project_ids: this.selectedProjects.map(p => p.project_id),
      target_user_id: this.selectedTargetUser.id,
      keep_copy: this.keepCopy,
      assignment_reason: this.assignmentReason || 'Attribution de projets'
    };

    this.projectHistoryService.assignProjectsToUser(assignmentData).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Attribution réussie',
          detail: response.message
        });

        // Fermer le dialog et réinitialiser
        this.closeAssignmentDialog();

        // Recharger la liste pour synchroniser les compteurs utilisateur.
        this.refresh();

        // Réinitialiser la sélection
        this.selectedProjects = [];
      },
      error: (error) => {
        console.error('Erreur lors de l\'attribution:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: error.error?.detail || 'Erreur lors de l\'attribution des projets'
        });
        this.assigningProjects = false;
      }
    });
  }

  /**
   * Ferme le dialog d'attribution
   */
  closeAssignmentDialog() {
    this.showAssignmentDialog = false;
    this.selectedTargetUser = null;
    this.keepCopy = false;
    this.assignmentReason = '';
    this.assigningProjects = false;
  }

  /**
   * Supprime un projet de la sélection
   */
  removeFromSelection(project: ProjectHistoryListItem) {
    this.allTableSelected = false;
    this.selectedProjects = this.selectedProjects.filter(p => p.project_id !== project.project_id);
  }

  /**
   * Vérifie si un projet est sélectionné
   */
  isProjectSelected(project: ProjectHistoryListItem): boolean {
    if (this.allTableSelected && this.canSelectProject(project)) {
      return true;
    }

    return this.selectedProjects.some(p => p.project_id === project.project_id);
  }

  /**
   * Toggle la sélection d'un projet
   */
  toggleProjectSelection(project: ProjectHistoryListItem, event: any) {
    if (!this.canSelectProject(project)) {
      return;
    }

    this.allTableSelected = false;

    if (event.checked) {
      if (!this.isProjectSelected(project)) {
        this.selectedProjects.push(project);
      }
    } else {
      this.removeFromSelection(project);
    }
  }

  /**
   * Sélectionne ou désélectionne tous les projets visibles
   */
  toggleAllSelection(event: any) {
    if (!this.hasSelectableVisibleProjects()) {
      return;
    }

    this.allTableSelected = false;
    const selectableVisibleProjects = this.filteredProjects.filter(project => this.canSelectProject(project));

    if (event.checked) {
      // Ajouter tous les projets visibles qui ne sont pas déjà sélectionnés
      selectableVisibleProjects.forEach(project => {
        if (!this.isProjectSelected(project)) {
          this.selectedProjects.push(project);
        }
      });
    } else {
      // Retirer tous les projets visibles de la sélection
      this.selectedProjects = this.selectedProjects.filter(selected =>
        !selectableVisibleProjects.some(visible => visible.project_id === selected.project_id)
      );
    }
  }

  /**
   * Vérifie si tous les projets visibles sont sélectionnés
   */
  areAllVisibleSelected(): boolean {
    if (this.allTableSelected) {
      return this.hasSelectableVisibleProjects();
    }

    const selectableVisibleProjects = this.filteredProjects.filter(project => this.canSelectProject(project));
    return selectableVisibleProjects.length > 0 &&
      selectableVisibleProjects.every(project => this.isProjectSelected(project));
  }

  /**
   * Vérifie si certains projets visibles sont sélectionnés
   */
  areSomeVisibleSelected(): boolean {
    if (this.allTableSelected) {
      return true;
    }

    const selectableVisibleProjects = this.filteredProjects.filter(project => this.canSelectProject(project));
    return selectableVisibleProjects.some(project => this.isProjectSelected(project));
  }

  canSelectEntireTable(): boolean {
    return this.isAdmin &&
      !this.allTableSelected &&
      this.areAllVisibleSelected() &&
      this.totalRecords > this.filteredProjects.length;
  }

  selectEntireTable(): void {
    if (!this.canSelectEntireTable()) {
      return;
    }

    this.allTableSelected = true;
  }

  clearSelection(): void {
    this.allTableSelected = false;
    this.selectedProjects = [];
  }

  getSelectedProjectsCount(): number {
    return this.allTableSelected ? this.totalRecords : this.selectedProjects.length;
  }

  private normalizeEmail(email: string | null | undefined): string {
    return (email || '').trim().toLowerCase();
  }

  private normalizePhoneSearchValue(value: string | null | undefined): string {
    return (value || '').replace(/\D+/g, '');
  }

  private isValidEmailFormat(email: string): boolean {
    return this.emailValidationPattern.test(email);
  }

  private syncMissingEmailValidationInBackground(): void {
    if (this.syncingEmailValidation || !this.projects?.length) {
      return;
    }

    const missingProjectIds = this.getProjectsMissingEmailValidation();

    if (!missingProjectIds.length) {
      return;
    }

    this.syncingEmailValidation = true;

    this.projectHistoryService.syncEmailValidation(missingProjectIds, true).subscribe({
      next: (response) => {
        this.applyStoredEmailValidation(response.items || []);
      },
      error: (error) => {
        console.warn('Synchronisation email indisponible pour project history', error);
      },
      complete: () => {
        this.syncingEmailValidation = false;
      }
    });
  }

  private getProjectsMissingEmailValidation(): string[] {
    return this.projects
      .filter(project => project.email_is_valid === null || project.email_is_valid === undefined)
      .map(project => project.project_id)
      .filter(Boolean);
  }

  private applyStoredEmailValidation(items: BulkEmailValidationSyncItem[]): void {
    if (!items?.length) {
      return;
    }

    const validationByProjectId = new Map(items.map(item => [item.project_id, item]));

    this.projects = this.projects.map(project => {
      const validation = validationByProjectId.get(project.project_id);
      return validation ? { ...project, ...validation } : project;
    });

    this.filteredProjects = this.filteredProjects.map(project => {
      const validation = validationByProjectId.get(project.project_id);
      return validation ? { ...project, ...validation } : project;
    });
  }

  getProjectEmailValidationState(project: ProjectHistoryListItem): 'valid' | 'invalid' | 'pending' {
    const email = this.normalizeEmail(project.email);

    if (project.email_is_valid === true) {
      return 'valid';
    }

    if (project.email_is_valid === false) {
      return 'invalid';
    }

    if (!email) {
      return 'invalid';
    }

    if (!this.isValidEmailFormat(email)) {
      return 'invalid';
    }

    return 'pending';
  }

  isProjectEmailInvalid(project: ProjectHistoryListItem): boolean {
    return this.getProjectEmailValidationState(project) === 'invalid';
  }
}
